/** Pure HTML templates for the marketplace web app.
 *
 *  Renders recipe and ingredient listings from Supabase data.
 *  All user-supplied strings go through `e()` for XSS defense.
 */

import type {
  RecipeBundleCatalogPack,
  RecipeBundleCatalogRecipe,
} from '@recued/contracts';
import {
  extractBulkPackRecipeRefs,
  getKernelOp,
  isRiskTier,
  kernelOpDelivers,
  OP_ENTITY_LABEL,
  parseOpId,
  recipeOutputSections,
  resolveRecipeBundleInstallPack,
  RISK_TIER_LABELS,
  RISK_TIER_RANK,
  stripCorePrefix,
} from '@recued/contracts';
import {
  marketplaceIngredientKind,
  marketplaceIngredientKindLabel,
  type MarketplaceIngredientKindFilter,
  type MarketplaceIngredientRenderState,
} from '@recued/ui-shared/marketplace';
import { renderMarkdown } from './markdown.js';

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

export interface RecipeListing {
  recipe_id: string;
  publisher_id: string;
  /** True when the publisher carries the marketplace "Certified
   *  author" badge. Derived from `publishers.certified` at query
   *  time — defaults to false on listings returned before the flag
   *  lands. */
  publisher_certified?: boolean;
  name: string;
  description: string;
  type: string;
  version: number;
  platforms: string[];
  tags: string[];
  download_count: number;
  /** Listing page views (D-180 AE flush, migration 026/027). Optional: rows
   *  fetched before the column shipped, and the roster projection, omit it. */
  view_count?: number;
  rating_avg: number;
  rating_count: number;
  /** The pack that owns this recipe's install (`<publisher>/<slug>`), lifted
   *  out of `recipe.metadata.recipe_bundle` by the lean projection. Without it
   *  a body-less corpus declares no memberships at all, and every bundled
   *  recipe's install CTA fails closed to "Workflow unavailable". */
  recipe_bundle?: string;
  /** True iff the body declares `provenance: false`. Migration 028 makes this a
   *  GENERATED column, so a table read carries it too and the browse queries no
   *  longer select `recipe` at all. Optional only because a hand-built row or a
   *  pre-028 read may omit it. */
  provenance_opted_out?: boolean;
  /** D-182 `recipe.depends_on` — the Tier-P packs this recipe hard-depends on.
   *  A generated column since 028 (it was projected from the body before), so
   *  the webclient's install deps box resolves from the catalog alone. */
  depends_on?: string[];
  /** ⚠ OPTIONAL because the browse corpus is lean. A card, a filter and a sort
   *  never need the body; only a DETAIL view does, and it fetches it on demand.
   *  `undefined` therefore means "not loaded yet", NOT "this recipe has no
   *  body" — render a pending state, never an empty one. */
  recipe?: {
    recipe_id: string;
    version: number;
    metadata: {
      name: string;
      description?: string;
      author: string;
      type?: string;
      supported_platforms?: string[];
      tags?: string[];
      readme?: string;
      /** v3 — author's source repo URL (https); issues / support
       *  route there, the marketplace hosts no issue tracking. */
      repo?: string;
      /** D-195 — namespaced identity of the pack that owns installation for
       *  this recipe: `<pack.publisher>/<pack.slug>`. Omit for standalone. */
      recipe_bundle?: string;
      /** D-b (slice 6c) — populated when this recipe is a fork of another
       *  published recipe. Rides inside the `recipe` JSONB
       *  (`metadata.fork_of`) — there is NO top-level `fork_of` column, so
       *  every existing `select=recipe` already carries it (no query
       *  change). `author` is the upstream publisher in marketplace
       *  vocabulary; shape mirrors `RecipeMetadata.fork_of`. */
      fork_of?: { recipe_id: string; author: string; version: number };
    };
    variables?: Record<string, unknown>;
    prefetch_steps?: unknown[];
    steps?: unknown[];
    output?: unknown;
    /** D-120 Phase 3 — provenance opt-out flag. Default-on; when
     *  the recipe declares `provenance: false`, the marketplace
     *  surfaces a "No provenance" badge so installers can see the
     *  recipe declines causal entity↔memory link emission. */
    provenance?: boolean;
    /** Author-declared install permissions (`read_memory`, `write_enrichment`,
     *  `read_connection_<vendor>`, `notification_send`, …) — the authoritative
     *  grant set Recued enforces at install. Body-sourced, so it survives the
     *  ingredient-manifest retirement that left the cloud unable to resolve
     *  per-ingredient grants. */
    requires?: string[];
    [key: string]: unknown;
  };
  created_at: string;
  /** Last publish / meta-edit time. Drives the "Updated" sort (12C); absent on
   *  rows returned before the column was selected → those fall back to
   *  `created_at`. */
  updated_at?: string;
}

/** A recipe's stored body. Present on rows read from the table; ABSENT on the
 *  lean browse corpus, which a detail view tops up on demand. */
export type RecipeBody = NonNullable<RecipeListing['recipe']>;

/** A pack's stored manifest — same present/absent split as `RecipeBody`. */
export type PackManifest = NonNullable<PackListing['manifest']>;

export interface IngredientListing {
  slug: string;
  publisher_id: string;
  /** True when the publisher carries the marketplace certified
   *  badge. Same semantics as `RecipeListing.publisher_certified`. */
  publisher_certified?: boolean;
  name: string;
  description: string;
  category: string;
  risk_tier: string;
  manifest: {
    slug: string;
    name: string;
    description: string;
    author: string;
    category: string;
    risk_tier: string;
    tags?: string[];
    [key: string]: unknown;
  };
  tags: string[];
  created_at: string;
}

export interface PackListing {
  slug: string;
  publisher_id: string;
  /** Same semantics as `RecipeListing.publisher_certified`. */
  publisher_certified?: boolean;
  name: string;
  description: string;
  version: number;
  pack_kind: string;
  download_count: number;
  /** Listing page views (D-180 AE flush, migration 026/027). Optional: rows
   *  fetched before the column shipped, and the roster projection, omit it. */
  view_count?: number;
  tags: string[];
  /** Pinned recipe membership, projected from the manifest by the lean
   *  corpus — under the SAME identity guard the local extraction applies, so a
   *  conflicting manifest still projects nothing and still fails closed. */
  recipe_refs?: Array<{ slug: string; version: number }>;
  /** Contents the pack SHOWS — the card's "N items". A GENERATED column since
   *  028, so it survives dropping `manifest` from every browse read (the
   *  manifest is 90-126 KB and 98.8% of it is one `contents[0].composition`
   *  blob no card has ever rendered). */
  visible_item_count?: number;
  /** EVERY entry in `manifest.contents`, visible or not. Generated (028).
   *  Distinct from `visible_item_count` on 65 of 927 packs — kept separate
   *  because this one is already on screen in the webclient's Discover and
   *  quietly redefining it would move a number there. */
  item_count?: number;
  /** The manifest's `service_kind`, so Discover can kind-section packs the way
   *  the Installed section does. Generated (028); NULL when the manifest
   *  declares none → the "Other" bucket. */
  service_kind?: string;
  /** ⚠ OPTIONAL for the same reason as `RecipeListing.recipe` — `undefined`
   *  means the body has not been fetched, not that the pack installs nothing. */
  manifest?: {
    slug: string;
    name: string;
    description?: string;
    pack_kind?: string;
    /** What the pack installs — recipes / ingredients, in display order. */
    contents?: Array<{
      type?: string; slug?: string; version?: number; visible?: boolean;
      /** D-220 — a `reception_template` entry carries the template inline. */
      template?: { template_ref?: string; name?: string };
    }>;
    /** Other packs this one builds on. */
    dependencies?: Array<{ type?: string; slug?: string; min_version?: number }>;
    [key: string]: unknown;
  };
  created_at: string;
  /** Last publish / meta-edit time — drives the "Updated" sort (12C). */
  updated_at?: string;
  /** 6d / §12B — render-ready aggregate trust summary precomputed at
   *  publish/seed (union scope · grants · data-touched + resolved content
   *  names). Absent on rows published before the precompute shipped → the
   *  pack detail degrades to a header-only render. Producer:
   *  `scripts/pack-trust.mjs` (`computePackTrust`). */
  trust_summary?: PackTrustSummary;
}

/** Narrow, request-local catalog context used by recipe-detail SSR. List and
 *  hydrated views already carry the full catalogs; a cold detail request fetches
 *  only the declaring recipes and the directly named pack instead. */
export interface RecipeBundleInstallContext {
  recipes: ReadonlyArray<RecipeBundleCatalogRecipe>;
  packs: ReadonlyArray<RecipeBundleCatalogPack & { listing: PackListing }>;
  /** Full listing rows for the directly named pack's recipes. These are
   *  display-only enrichment: bundle/install authority stays with `recipes`
   *  + `packs` above. Cold SSR fetches them narrowly; hydrated views already
   *  have the full recipe catalogue and can omit this field. */
  recipeListings?: ReadonlyArray<RecipeListing>;
}

/** One contained ingredient's grant row in a pack's aggregate trust — all
 *  fields pre-resolved to display form at publish so the renderer just
 *  iterates (owner: "easy to read when render"). Mirrors `RecipeGrant` but
 *  with operations pre-composed into `{ text, title }`. */
export interface PackGrant {
  name: string;
  kindLabel: string;
  /** Risk tier label form ("Read"/"Write"/"Admin"/"Destructive") or ''. */
  risk: string;
  external: boolean;
  reversible: boolean;
  operations: { text: string; title: string }[];
}

/** 6d / §12B — the precomputed, render-ready aggregate trust for a pack.
 *  Union across all contained recipes × ingredients, computed ONCE at
 *  publish/seed (`scripts/pack-trust.mjs`) so the detail page renders O(1).
 *  Every field is already in display form — labels resolved, lists deduped,
 *  grants sorted, names attached. The renderer maps 1:1, no re-derivation. */
/** What a precomputed content row can be. `reception_template` (D-220) is a
 *  pack-shipped intake form: installed with the pack, reviewed in Reception,
 *  no public page of its own. Rows seeded before it existed carry only the
 *  first two; the renderer falls back for a kind it does not know rather than
 *  printing `undefined`. */
export type PackTrustContentKind = 'recipe' | 'ingredient' | 'reception_template';

export interface PackTrustSummary {
  /** Highest risk tier label across all contained ingredients, or ''. */
  highestRisk: string;
  externalWrites: boolean;
  anyReversible: boolean;
  grants: PackGrant[];
  /** Data surfaces touched (kind labels), credential scopes, warehouse refs. */
  connections: string[];
  vaultScopes: string[];
  warehouseRefs: string[];
  /** "What this pack installs" — display order, names resolved. */
  contents: { kind: PackTrustContentKind; ref: string; name: string }[];
  recipeCount: number;
  ingredientCount: number;
  /** Contents that could not be resolved to a trust signal at compute time. */
  unresolved: number;
}

export interface PublisherProfile {
  id: string;
  display_name?: string | null;
  bio?: string | null;
  website?: string | null;
  certified?: boolean;
  created_at?: string | null;
  /** Future public profile field. The live schema does not carry avatar
   *  URLs yet, so renderer paths must keep the initials fallback. */
  avatar_url?: string | null;
}

/** Facet hub dimensions. `platform` only, deliberately.
 *
 *  Measured against the live catalogue 2026-07-22. Tags are excluded outright:
 *  1,917 distinct, only 102 above the floor, and the largest are ATTRIBUTES
 *  rather than subjects (`read-only` 796, `approval` 664) — ~1,900 near-empty
 *  pages would spend crawl budget the detail pages need. Platforms are included
 *  but heavily gated; see MIN_FACET_LISTINGS for why the gate is where it is.
 *
 *  This vocabulary lives HERE, not in the SSR worker, because three surfaces
 *  read it and they must not be able to disagree: the worker routes + sitemaps
 *  from it, and the SPA re-derives the same hubs when it hydrates on top of a
 *  server-rendered hub page. A copy in either place would rot silently — the
 *  server would publish a hub the client then renders as missing. */
export type FacetKind = 'platform';

/** The listing column each facet counts. Also the reservation list: a segment
 *  named here belongs to the facet namespace under `/recipes/`, never to a
 *  listing slug (see the worker's `parseDetailPath`). */
export const FACET_COLUMN: Record<FacetKind, string> = { platform: 'platforms' };

/** One facet value and how many listings carry it, corpus-wide. */
export interface FacetCount {
  value: string;
  count: number;
}

export type View =
  | 'landing'
  | 'recipes'
  | 'packs'
  | 'facet'
  | 'facet-index'
  | 'recipe-detail'
  | 'pack-detail'
  | 'publisher';
export type SortKey = 'popular' | 'newest' | 'updated' | 'name';

export interface MarketplaceState {
  view: View;
  loading: boolean;
  error: string | null;
  recipes: RecipeListing[];
  ingredients: IngredientListing[];
  packs: PackListing[];
  /** Optional targeted projection for a recipe-detail render. */
  recipeBundleInstallContext?: RecipeBundleInstallContext;
  publishers: PublisherProfile[];
  search: string;
  platformFilter: string | null;
  tagFilter: string | null;
  /** 12C — recipe/pack publisher facet (publisher_id / handle). */
  publisherFilter: string | null;
  /** 12C — pack `pack_kind` facet (the pack list's primary filter). */
  packKindFilter: string | null;
  kindFilter: MarketplaceIngredientKindFilter | null;
  sort: SortKey;
  /** 1-based browse page. Filters reset it to 1. */
  page: number;
  /** Slug of the detail item being viewed. */
  detailSlug: string | null;
  /** Set ONLY when the database already narrowed the rows to one page (the SSR
   *  list routes). See `CorpusTotals`. */
  totals?: CorpusTotals;
  /** How many rows the ACTIVE QUERY matched, per kind — set by the hydrated SPA,
   *  which searches server-side (`/catalog/search`) and therefore holds one page
   *  of matches rather than a filtered catalogue.
   *
   *  Distinct from `totals`, and collapsing the two breaks a count line either
   *  way: `totals` is the CATALOGUE (the stats bar, "View all N recipes"), this
   *  is the RESULT SET (the pager, and the "42 of 1,932" denominator's
   *  numerator). Absent ⇒ nothing narrowed the set, or the rows in hand are the
   *  whole answer. */
  matched?: { recipes?: number; packs?: number };
  /** The server's DROP-ONE-OUT facet counts for the current query, keyed by
   *  facet key (`platform` / `tag` / `publisher` / `pack_kind` / `service_kind`).
   *
   *  Absent ⇒ count the rows in hand instead. That fallback is not vestigial:
   *  `renderMarketplace` also runs inside the SSR worker, which renders a page
   *  of rows and has no facet response to pass. */
  facets?: Record<string, FacetCount[]>;
  /** Candidates for a recipe detail's "Works well with" strip.
   *
   *  Was `state.recipes` — the whole catalogue, scored by platform + tag overlap.
   *  With the corpus gone the SPA fetches a small platform-matched set instead,
   *  and the scoring below ranks THOSE. Absent ⇒ fall back to the rows in hand,
   *  which on an SSR detail render is the one row being viewed, so the section
   *  omits itself exactly as it always has server-side. */
  related?: RecipeListing[];
  /** Landing-page search preview — the top few matches for `search`, fetched
   *  from the endpoint. Absent while a search is in flight (and always on SSR,
   *  which normalises query params away), which is what keeps the page from
   *  flashing "Nothing matches that search yet" before the answer arrives. */
  searchPreview?: { recipes: RecipeListing[]; packs: PackListing[] };
  /** Set on a facet hub render (`/recipes/platform/<x>`): the DATABASE already
   *  narrowed the rows to this facet, so every self-link must stay inside it —
   *  pagination that walked back to `/recipes` would drop the reader out of the
   *  set they are reading. */
  facet?: { kind: FacetKind; value: string };
  /** Set on the hub-of-hubs render (`/recipes/platform`). `values` carries every
   *  facet value that cleared the floor with its whole-corpus count — present
   *  when the SERVER counted them, absent when the rows in hand ARE the corpus
   *  and the renderer counts them itself (the hydrated SPA), exactly as `totals`
   *  works for the lists.
   *
   *  What must never happen is counting a PAGE: a hub index measured over
   *  forty-eight rows would size every platform at a fiftieth of its real count
   *  and drop all of them below the floor. Nothing on a browse route sets this. */
  facetIndex?: { kind: FacetKind; values?: FacetCount[] };
  /** See `PublisherStats` — set on a server-paged publisher page. */
  publisherStats?: PublisherStats;
  /** Why the on-demand fetch of the current detail view's body failed. The
   *  browse corpus is lean, so a detail view pulls its own body; a failure has
   *  to say so rather than render an empty listing as a complete one. */
  detailBodyError?: string | null;
}

/** Whole-corpus counts, carried when `recipes`/`packs` hold ONE PAGE rather than
 *  the catalogue. Two things stop being true the moment the database does the
 *  paging, and both fail silently:
 *
 *   • `recipes.length` / `packs.length` are this PAGE's size, not the corpus.
 *     Every count line, stat, and — worst — the PAGE COUNT must read these
 *     totals instead: `pageCount(48)` is 1, so the pagination control computes
 *     a single page and disappears, stranding readers and crawlers on page 1
 *     of 55 with no link onward.
 *   • the rows must NOT be sliced again. They are already the window.
 *
 *  Absent ⇒ the state holds the whole catalogue and pagination happens in
 *  memory (the SPA after hydration, and every unit test of the renderer). */
/** A publisher page's aggregates, computed over that publisher's WHOLE
 *  catalogue while the grids below render one window of it. Present ⇒ the rows
 *  in `state` are a page, and every number here must come from this rather than
 *  from counting them. */
export interface PublisherStats {
  recipes: number;
  packs: number;
  installs: number;
  ratings: number;
  latest: string | null;
}

export interface CorpusTotals {
  recipes: number;
  packs: number;
  /** Registered marketplace publishers. Read from the publishers TABLE, not
   *  derived from the listings on this page — a page of 48 rows knows nothing
   *  about who else publishes. */
  publishers: number;
}

export const initialState = (): MarketplaceState => ({
  view: 'landing',
  loading: true,
  error: null,
  recipes: [],
  ingredients: [],
  packs: [],
  search: '',
  platformFilter: null,
  tagFilter: null,
  publisherFilter: null,
  packKindFilter: null,
  kindFilter: null,
  // 'newest' (created_at DESC), not 'popular' (owner, 2026-07-22). With one
  // listing in 3,551 carrying a download and a single publisher, both the
  // download count and the certified boost are inert — every row ties, so
  // 'popular' degrades to an accident of insertion order. `updated_at` was
  // rejected for the sharper reason: it is publisher-writable, so ranking on it
  // hands anyone a top slot for re-saving their listing. `created_at` cannot be
  // gamed after the fact.
  //
  // MUST MATCH the `order=` on the catalogue queries (ssr/worker.ts,
  // marketplace.ts). The DB decides which rows a page contains; this decides how
  // they are shown. Disagreement is invisible today — the client re-sorts the
  // whole set — but becomes missing listings the moment the list is paginated.
  sort: 'newest',
  page: 1,
  detailSlug: null,
  publishers: [],
});

// ────────────────────────────────────────────────────────────────
// Escape
// ────────────────────────────────────────────────────────────────

const ESC: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};

export const e = (s: string): string => s.replace(/[&<>"']/g, (c) => ESC[c] ?? c);

const WEBCLIENT_RECIPES_URL = 'https://app.recued.com/#recipes';
const WEBCLIENT_PACKS_URL = 'https://app.recued.com/#packs';
const KITCHEN_URL = 'https://app.recued.com/#kitchen';
const DASHBOARD_PUBLISH_URL = 'https://dashboard.recued.com/#/publish';
const DASHBOARD_URL = 'https://dashboard.recued.com';
const GITHUB_URL = 'https://github.com/recued/recued';
export const DEMO_URL = 'https://demo.recued.com/';

/** Apex surfaces reachable from the shared shell. The marketplace dispatcher
 *  maps its view → one of these to highlight the active left-nav item; static
 *  content + funnel routes pass their own key (or none, e.g. the 404). */
export type ApexNavKey = 'how-it-works' | 'recipes' | 'packs' | 'bridge' | 'pricing' | 'docs' | 'publish';

/** Left-nav items (R4 apex shell): How it works · Recipes · Packs · Bridge ·
 *  Pricing · Docs. The CTA cluster (GitHub · Publish · Sign in · Get started) renders
 *  separately. */
const APEX_NAV: { key: ApexNavKey; href: string; label: string }[] = [
  { key: 'how-it-works', href: '/how-recued-works', label: 'How it works' },
  { key: 'recipes', href: '/recipes', label: 'Recipes' },
  { key: 'packs', href: '/packs', label: 'Packs' },
  { key: 'bridge', href: '/bridge', label: 'Bridge' },
  { key: 'pricing', href: '/pricing', label: 'Pricing' },
  { key: 'docs', href: '/docs', label: 'Docs' },
];

/** Inline GitHub mark — reused by the header icon-link. */
const GITHUB_MARK_SVG =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 .3a12 12 0 0 0-3.8 23.38c.6.1.83-.26.83-.57v-2.23c-3.34.73-4.04-1.42-4.04-1.42-.55-1.39-1.34-1.76-1.34-1.76-1.08-.74.09-.73.09-.73 1.2.08 1.84 1.24 1.84 1.24 1.07 1.83 2.8 1.3 3.49 1 .1-.78.42-1.3.76-1.6-2.67-.31-5.47-1.34-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.14-.3-.54-1.52.1-3.18 0 0 1-.32 3.3 1.23a11.5 11.5 0 0 1 6.02 0c2.28-1.55 3.29-1.23 3.29-1.23.64 1.66.24 2.88.12 3.18a4.65 4.65 0 0 1 1.23 3.22c0 4.61-2.8 5.62-5.48 5.92.42.36.81 1.1.81 2.22v3.29c0 .31.22.68.83.57A12 12 0 0 0 12 .3"/></svg>';

const publisherHref = (handle: string): string =>
  `/publishers/${encodeURIComponent(handle)}`;

const publisherPublicUrl = (handle: string): string =>
  `https://recued.com${publisherHref(handle)}`;

// Path-form handoffs (XS-1). A recipe keeps its explicit one-shot install
// intent; a pack opens `#packs/<slug>` because that durable detail owns the
// consent/grant review and calls server install-by-slug on confirmation. IDs
// are encoded as one segment so `publisher/slug` round-trips intact.
const recipeInstallHref = (recipeId: string): string =>
  `${WEBCLIENT_RECIPES_URL}/install/${encodeURIComponent(recipeId)}`;

const packInstallHref = (slug: string): string =>
  `${WEBCLIENT_PACKS_URL}/${encodeURIComponent(slug)}`;

// ────────────────────────────────────────────────────────────────
// Supabase config
// ────────────────────────────────────────────────────────────────

/** Baked fallback = the PRODUCTION project. The SSR worker injects its
 *  env-scoped values into every hydrating document as `window.RECUED_SUPABASE`
 *  (see `document()` in ssr/render.ts), so these literals only serve a shell
 *  that carries no injection — local dev and tests. Publishable/anon keys
 *  only — public and RLS-gated by design; never a service-role key. */
export const DEFAULT_SUPABASE_URL = 'https://zzremnbtteclikewrlbr.supabase.co';
export const DEFAULT_SUPABASE_ANON_KEY = 'sb_publishable_1Fcs9ugXnly0RUT84rFRKw_FX4MjbKi';

export interface SupabaseRuntimeConfig {
  url: string;
  key: string;
}

/** Client-side Supabase endpoint resolution: prefer the SSR-injected per-env
 *  config, fall back to the baked production default. Without the injection
 *  preference, a staging page's SPA would silently read PRODUCTION data — the
 *  bundle is env-agnostic, only the serving worker knows which project backs
 *  the host it serves. */
export const supabaseConfig = (): SupabaseRuntimeConfig => {
  const injected = (globalThis as { RECUED_SUPABASE?: Partial<SupabaseRuntimeConfig> })
    .RECUED_SUPABASE;
  if (
    injected
    && typeof injected.url === 'string' && injected.url !== ''
    && typeof injected.key === 'string' && injected.key !== ''
  ) {
    return { url: injected.url, key: injected.key };
  }
  return { url: DEFAULT_SUPABASE_URL, key: DEFAULT_SUPABASE_ANON_KEY };
};

// ────────────────────────────────────────────────────────────────
// Subtemplates
// ────────────────────────────────────────────────────────────────

/** Shared apex header (R4 shell). `active` highlights the matching left-nav
 *  item. The CTA cluster has exactly ONE filled button (Get started → the cold
 *  visitor's funnel); Publish → the apex explainer; Sign in → dashboard auth.
 *  No "Open Recued"/app links live on the apex (AX-3). */
export const renderHeader = (active?: ApexNavKey): string => `
  <header class="mp-header">
    <a href="/" class="mp-brand" aria-label="Recued home">Recued</a>
    <nav class="mp-nav" aria-label="Primary navigation">
      ${APEX_NAV.map((item) => `<a href="${item.href}"${active === item.key ? ' class="active" aria-current="page"' : ''}>${item.label}</a>`).join('\n      ')}
    </nav>
    <div class="mp-header-cta">
      <button type="button" class="mp-theme-toggle" data-recued-theme-toggle aria-label="Color theme — click to switch between system, light, and dark" title="Color theme"></button>
      <a href="${GITHUB_URL}" target="_blank" rel="noopener" class="header-icon-link" title="Source on GitHub" aria-label="Source on GitHub">${GITHUB_MARK_SVG}</a>
      <a href="/publish" class="mp-signin-link${active === 'publish' ? ' active' : ''}">Publish</a>
      <a href="${DASHBOARD_URL}" class="mp-signin-link">Sign in</a>
      <a href="/get-started" class="btn-cta-sm">Get started</a>
    </div>
    <details class="mp-mobile-nav">
      <summary><span>Menu</span><span class="mp-mobile-nav-icon" aria-hidden="true"></span></summary>
      <div class="mp-mobile-nav-panel">
        <nav aria-label="Mobile navigation">
          ${APEX_NAV.map((item) => `<a href="${item.href}"${active === item.key ? ' class="active" aria-current="page"' : ''}>${item.label}</a>`).join('\n          ')}
        </nav>
        <div class="mp-mobile-nav-actions">
          <a href="/publish">Publish</a>
          <a href="${DASHBOARD_URL}">Sign in</a>
          <a href="${GITHUB_URL}" target="_blank" rel="noopener">GitHub</a>
          <button type="button" class="mp-theme-toggle" data-recued-theme-toggle aria-label="Color theme — click to switch between system, light, and dark">Theme:&nbsp;</button>
        </div>
      </div>
    </details>
  </header>
`;

/** Map a marketplace view to its active apex-nav key (Pricing/Docs are their
 *  own documents, highlighted by those routes — not the marketplace dispatcher). */
const activeNavOf = (view: MarketplaceState['view']): ApexNavKey | undefined => {
  // The facet views are recipe browsing under a narrower URL — a reader on a
  // platform hub is in the Recipes section and the nav should say so.
  if (view === 'recipes' || view === 'recipe-detail' || view === 'facet' || view === 'facet-index') {
    return 'recipes';
  }
  if (view === 'packs' || view === 'pack-detail') return 'packs';
  return undefined;
};

/** Shared apex footer (R4): Product · Publishers · Legal · Community columns +
 *  a colophon. Every folded apex route is reachable here so the marketplace is
 *  not the only crawl entry point (SW-6). */
export const renderFooter = (): string => `
  <footer class="mp-footer">
    <div class="mp-footer-cols">
      <div class="mp-footer-col">
        <h4>Product</h4>
        <a href="/how-recued-works">How it works</a>
        <a href="/get-started">Get started</a>
        <a href="${DEMO_URL}" target="_blank" rel="noopener">Interactive demo</a>
        <a href="/recipes">Recipes</a>
        ${/* The hub-of-hubs. In the footer because that is the one place it is
              reachable from EVERY page — the twenty platform hubs it indexes
              spent their first week reachable only from the sitemap and from
              the detail pages that happened to use them. */''}
        <a href="/recipes/platform">Recipes by platform</a>
        <a href="/packs">Packs</a>
        <a href="/bridge">Bridge</a>
        <a href="/pricing">Pricing</a>
        <a href="/docs">Docs</a>
      </div>
      <div class="mp-footer-col">
        <h4>Publishers</h4>
        <a href="/publish">Publish</a>
        <a href="${DASHBOARD_URL}">Sign in</a>
        <a href="${DASHBOARD_URL}">Dashboard</a>
      </div>
      <div class="mp-footer-col">
        <h4>Legal</h4>
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="/bridge-terms">Bridge Terms</a>
        <a href="/billing">Billing &amp; Refunds</a>
        <a href="/support">Support</a>
      </div>
      <div class="mp-footer-col">
        <h4>Community</h4>
        <a href="${GITHUB_URL}" target="_blank" rel="noopener">GitHub</a>
      </div>
    </div>
    <div class="mp-footer-colophon">Recued holds the space between you and AI. Free, open, BYOK. Operated by RECUED LDA, Macao SAR.</div>
  </footer>
`;

/** Wrap a page body in the shared apex chrome (header + footer). Used by the
 *  marketplace SSR/SPA, the static-content documents, and the 404 so every apex
 *  surface shares one shell. */
export const renderAppShell = (body: string, active?: ApexNavKey): string => `
  <div class="marketplace">
    ${renderHeader(active)}
    <main id="main-content">${body}</main>
    ${renderFooter()}
  </div>
`;

// ────────────────────────────────────────────────────────────────
// Landing page
// ────────────────────────────────────────────────────────────────

const findPublisherProfile = (
  state: Pick<MarketplaceState, 'publishers'>,
  handle: string,
): PublisherProfile | null =>
  state.publishers.find((p) => p.id === handle) ?? null;

const publisherDisplayName = (
  state: Pick<MarketplaceState, 'publishers'>,
  handle: string,
): string => {
  const profile = findPublisherProfile(state, handle);
  return profile?.display_name?.trim() || handle;
};

const renderPublisherLink = (
  state: Pick<MarketplaceState, 'publishers'>,
  handle: string,
): string => `<a href="${publisherHref(handle)}" class="mp-publisher-link">${e(publisherDisplayName(state, handle))}</a>`;

const publisherInitials = (
  state: Pick<MarketplaceState, 'publishers'>,
  handle: string,
): string => {
  const label = publisherDisplayName(state, handle);
  const words = label.split(/[\s_-]+/).filter(Boolean);
  const raw = words.length >= 2
    ? `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`
    : label.slice(0, 2);
  return raw.toUpperCase();
};

const listingPublisherHandles = (state: MarketplaceState): string[] => [
  ...new Set([
    ...state.recipes.map((r) => r.publisher_id),
    ...state.packs.map((p) => p.publisher_id),
  ]),
].sort((a, b) => publisherDisplayName(state, a).localeCompare(publisherDisplayName(state, b)));

const publisherRecipes = (state: MarketplaceState, handle: string): RecipeListing[] =>
  state.recipes.filter((r) => r.publisher_id === handle);

const publisherInstallTotal = (recipes: RecipeListing[]): number =>
  recipes.reduce((sum, r) => sum + (r.download_count ?? 0), 0);

const latestPublishAt = (
  recipes: readonly { created_at?: string | null }[],
  others: readonly { created_at?: string | null }[],
): string | null => {
  const dates = [...recipes.map((r) => r.created_at), ...others.map((o) => o.created_at)]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .sort((a, b) => b.localeCompare(a));
  return dates[0] ?? null;
};

const renderDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No public posts yet';

const renderPublisherAvatar = (
  state: Pick<MarketplaceState, 'publishers'>,
  handle: string,
): string => {
  const profile = findPublisherProfile(state, handle);
  const alt = `${publisherDisplayName(state, handle)} avatar`;
  if (profile?.avatar_url) {
    return `<img class="publisher-avatar" src="${e(profile.avatar_url)}" alt="${e(alt)}" />`;
  }
  return `<div class="publisher-avatar publisher-avatar-fallback" aria-label="${e(alt)}">${e(publisherInitials(state, handle))}</div>`;
};

const renderPublisherStrip = (state: MarketplaceState): string => {
  // The publishers TABLE first, listings only as a fallback. Deriving handles
  // from the listings in hand was fine while the landing held the whole
  // catalogue; it now holds a 50-row featured pool, and a publisher whose
  // listings miss that pool would silently vanish from the strip. The table is
  // one row per publishing account — the smallest read on the page.
  const fromTable = [...state.publishers]
    .map((p) => p.id)
    .sort((a, b) => publisherDisplayName(state, a).localeCompare(publisherDisplayName(state, b)));
  const publishers = (fromTable.length > 0 ? fromTable : listingPublisherHandles(state)).slice(0, 4);
  if (publishers.length === 0) {
    return `
      <div class="landing-publish-callout">
        <h3>Publish under your own handle</h3>
        <p>Publishing is free. A recued.com account reserves the namespace that owns your marketplace slugs.</p>
        <a href="/publish" class="btn-cta-secondary">Publish your recipe</a>
      </div>
    `;
  }
  return `
    <div class="landing-publisher-strip">
      ${publishers.map((handle) => {
        const recipes = publisherRecipes(state, handle);
        const packs = state.packs.filter((p) => p.publisher_id === handle);
        const profile = findPublisherProfile(state, handle);
        const certified = profile?.certified
          || recipes.some((r) => r.publisher_certified)
          || packs.some((p) => p.publisher_certified);
        return `
          <a href="${publisherHref(handle)}" class="landing-publisher">
            ${renderPublisherAvatar(state, handle)}
            <span>
              <strong>${e(publisherDisplayName(state, handle))}</strong>
              <em>@${e(handle)}${certified ? ' · Certified' : ''}</em>
            </span>
          </a>
        `;
      }).join('')}
    </div>
  `;
};

const renderLanding = (state: MarketplaceState): string => {
  // `totals` wins where present: the landing reads a small FEATURED POOL, not
  // the catalogue, so counting the rows in hand would put "View all 50 recipes"
  // on the homepage of a 2,624-recipe marketplace.
  const recipeCount = state.totals?.recipes ?? state.recipes.length;
  const packCount = state.totals?.packs ?? state.packs.length;
  // Already in featured order — `LISTING_FEATURED_ORDER`, read by BOTH the SSR
  // worker and the hydrated SPA so the three cards do not change under the
  // reader on hydration. No re-sort here: this renderer used to rank the pool
  // itself, which is exactly how the two could disagree.
  const featuredRecipes = state.recipes.slice(0, 3);
  const featuredPacks = state.packs.slice(0, 3);
  // Absent while a search is in flight — see `MarketplaceState.searchPreview`.
  const preview = state.searchPreview;
  const searchRecipes = preview?.recipes ?? [];
  const searchPacks = preview?.packs ?? [];

  return `
    <section class="landing-hero">
      <p class="landing-eyebrow">A local control plane for AI</p>
      <h1 class="landing-tagline">Recued holds the space between you and AI.</h1>
      <p class="landing-sub landing-lead">
        Give an AI agent the key to your email or CRM, and it can do anything that key allows.
        Recued is a self-hosted gateway between AI agents and your accounts, and the agent never
        gets the key. It gets only the tools you grant it. By default, anything that writes,
        sends, or deletes waits for your yes.
      </p>
      <div class="landing-proof" aria-label="Product principles">
        <span>Local-first</span>
        <span>Permission-gated</span>
        <span>Free &amp; open</span>
        <span>Bring your own keys</span>
      </div>
      <figure class="landing-diagram">
        <a href="/assets/recued-control-plane-diagram.svg" class="landing-diagram-link" target="_blank" rel="noopener" aria-label="Open the control-plane diagram at full size">
          <img
            src="/assets/recued-control-plane-diagram.svg"
            width="1440" height="1050" loading="eager" decoding="async"
            alt="Recued as a local control plane, running on your machine and encrypted at rest. A central Recued server holds your data (mail, calendar, files, contacts, tasks, notes, CRM, the things you own, memory and timeline) and does jobs of its own: a chat you reach in the app, by email, in Slack or Telegram, and recipes that run by hand, on a schedule, or when something happens. Agents, people and those jobs all pass one Contracts and Gateway boundary: a contract holds one key and grants nothing until you choose, unknown callers fail closed, and a master pause halts everything. AI writes ask first, one at a time, per batch, or per session, and Logs record every call before it runs. It sits between your own systems (reached through Connections and packs, with keys on your server or a broker you pick), people arriving through Reception and Messenger, outside AI agents and paying customers calling in over MCP or the OpenAI API, and AI providers, with personal data aliased before it leaves and restored locally."
          />
        </a>
        <figcaption>
          Agents and people ask, and Recued does jobs of its own. All of it passes one gate: grants
          are checked, personal data is shielded before it reaches an LLM, and writes wait for your
          approval — one, a batch, or a whole session, answered in the app, by email, Slack or
          Telegram. Recued acts locally with your keys and records every run. Running work can be
          watched live, killed, or paused outright.
          <span class="landing-diagram-hint">Open the diagram to inspect it at full size.</span>
        </figcaption>
      </figure>
      <figure class="landing-diagram">
        <a href="/assets/recued-chat-flow.svg" class="landing-diagram-link" target="_blank" rel="noopener" aria-label="Open the chat diagram at full size">
          <img
            src="/assets/recued-chat-flow.svg"
            width="1920" height="1320" loading="lazy" decoding="async"
            alt="Recued answers supported lookups directly from saved records and prepares relevant facts for questions that need AI. One example answers a contact email lookup locally. Another protects Maya's name as Person 1 and brings together her insurance policy expiry on 18 November and her trip starting on 20 November. AI can answer that the policy expires two days before Maya leaves, without additional lookups when that context is sufficient. If more information is needed, tools supply it before AI continues. The examples and data are fictional."
          />
        </a>
        <figcaption>
          Recued has its own chat: in the web app, by email, and in Slack and Telegram. It answers
          from your saved records first. A lookup like "What is Maya's email?" needs no AI at all.
          When a question does need AI, Recued swaps out the personal details and brings the
          relevant facts along, so the model has them before it starts.
          <a href="${DEMO_URL}app/?example=maya-answers" target="_blank" rel="noopener">Replay Maya's questions</a> in the demo.
          <span class="landing-diagram-hint">Open the diagram to inspect it at full size.</span>
        </figcaption>
      </figure>
      <div class="landing-cta">
        <a href="/get-started" class="btn-cta">Get started</a>
        <a href="${DEMO_URL}" class="btn-cta-secondary" target="_blank" rel="noopener">See the demo</a>
        <a href="/how-recued-works" class="btn-cta-secondary">How Recued works</a>
        <a href="/recipes" class="btn-cta-secondary">Browse the marketplace</a>
      </div>
    </section>

    <section class="landing-search-panel">
      <div>
        <h2>Search the marketplace</h2>
        <p>Find installable recipes, packs, publishers, platforms, and tags.</p>
      </div>
      ${renderSearch(state)}
    </section>

    ${state.search && preview ? `
      <section class="landing-section">
        <div class="landing-section-head">
          <h2>Search results</h2>
          <a href="/recipes">Open full recipe list</a>
        </div>
        ${searchRecipes.length === 0 && searchPacks.length === 0
          ? '<div class="mp-empty">Nothing matches that search yet.</div>'
          : `<div class="mp-grid">
              ${searchRecipes.map((r) => renderRecipeCard(r, state)).join('')}
              ${searchPacks.map((p) => renderPackCard(p, state)).join('')}
            </div>`}
      </section>
    ` : ''}

    <section class="landing-section">
      <div class="landing-section-head">
        <h2>Featured recipes</h2>
        <a href="/recipes">View all ${recipeCount} recipe${recipeCount === 1 ? '' : 's'}</a>
      </div>
      ${featuredRecipes.length > 0
        ? `<div class="mp-grid">${featuredRecipes.map((r) => renderRecipeCard(r, state)).join('')}</div>`
        : '<div class="mp-empty">No published recipes yet. Be the first publisher in the marketplace.</div>'}
    </section>

    <section class="landing-section">
      <div class="landing-section-head">
        <h2>Featured packs</h2>
        <a href="/packs">View all ${packCount} pack${packCount === 1 ? '' : 's'}</a>
      </div>
      ${featuredPacks.length > 0
        ? `<div class="mp-grid">${featuredPacks.map((p) => renderPackCard(p, state)).join('')}</div>`
        : '<div class="mp-empty">No published packs yet.</div>'}
    </section>

    <section class="landing-section">
      <div class="landing-section-head">
        <h2>Publishers</h2>
        <a href="/publish">Publish your recipe</a>
      </div>
      ${renderPublisherStrip(state)}
    </section>

    <section class="landing-note">
      <h2>Marketplace content is free.</h2>
      <p>Publishing requires signup because the account owns your namespace. Pro is a convenience bundle for reserved handles, DDNS, and ACME setup; it is not a marketplace content gate.</p>
      <div class="landing-cta">
        <a href="/get-started" class="btn-cta">Get started</a>
        <a href="/publish" class="btn-cta-secondary">Publish</a>
      </div>
    </section>
  `;
};

// ────────────────────────────────────────────────────────────────
// Marketplace subtemplates
// ────────────────────────────────────────────────────────────────

const renderHero = (): string => `
  <section class="mp-hero">
    <span class="mp-eyebrow">Community catalog</span>
    <h1>Marketplace</h1>
    <p>Inspect what a workflow does, what it installs, and what it can access before bringing it into Recued.</p>
  </section>
`;

/** Turn a platform slug into something a heading can say. `cockroachdb-cloud`
 *  → `Cockroachdb Cloud`. Imperfect for camel-cased brands (HubSpot reads as
 *  Hubspot) — the catalogue stores slugs and no display-name source exists for
 *  166 platforms, and inventing a hand-kept brand map would rot on the first
 *  publisher who adds one. */
export const facetLabel = (value: string): string =>
  value.split(/[-_.]/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/** A platform earns its own page at this many listings — not merely at "enough
 *  to not look empty".
 *
 *  Owner call 2026-07-22, and the data is unambiguous once you look at where
 *  the vocabulary comes from: every generator hardcodes
 *  `supported_platforms: ['<vendor>']`, and 2,414 of 2,624 recipes carry
 *  exactly one. `platform` is the VENDOR SLUG, so its cardinality tracks the
 *  number of integrations built — 166 today, +1 per generator, forever. A page
 *  per value scales with how much we ship, not with what anyone searches for.
 *
 *  At 5 this floor published 160 pages, which is the whole vocabulary minus
 *  rounding — it was preventing empty pages, not deciding anything. At 25 it
 *  publishes 20: hubspot(102) down to hibob(25), each a destination worth
 *  landing on. The other 146 are better served by the search box, which
 *  filters them fine and needs no page. */
export const MIN_FACET_LISTINGS = 25;

export const facetPath = (facet: FacetKind, value: string): string =>
  `/recipes/${facet}/${encodeURIComponent(value)}`;

/** The hub-of-hubs — the one browsable entry point into the hubs. Without it
 *  the only paths in are the sitemap and the platform chips on 2,624 detail
 *  pages, which is a set of pages no reader can find. */
export const facetIndexPath = (facet: FacetKind): string => `/recipes/${facet}`;

/** Which facet values earn a hub page, counted over whatever rows are handed in.
 *
 *  FOUR consumers and one definition, which is the whole point: the worker's
 *  hub-of-hubs page, the sitemap (a listed URL the worker would 404 is a
 *  self-inflicted crawl error), the route's own floor check, and the SPA after
 *  hydration. Rows are loose records so the worker can pass a lean Supabase
 *  read and the SPA can pass its catalogue projection — both carry the facet
 *  column and nothing else is needed.
 *
 *  Sorted by count then name so the output is byte-stable across runs — a
 *  sitemap or a hub grid that reshuffles on every read looks like churn. */
export const qualifyingFacets = (
  rows: ReadonlyArray<unknown>,
  facet: FacetKind = 'platform',
): FacetCount[] => {
  const counts = new Map<string, number>();
  for (const row of rows) {
    // `unknown` rows on purpose: the worker passes a lean Supabase read and the
    // SPA passes typed `RecipeListing`s. Both carry the facet column; anything
    // that does not is skipped rather than trusted.
    const values = (row as Record<string, unknown> | null)?.[FACET_COLUMN[facet]];
    if (!Array.isArray(values)) continue;
    // A row can repeat a value; a facet page would still list it once.
    for (const v of new Set(values)) {
      if (typeof v !== 'string' || !v) continue;
      counts.set(v.toLowerCase(), (counts.get(v.toLowerCase()) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .filter(([, n]) => n >= MIN_FACET_LISTINGS)
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
};

/** `qualifyingFacets`, but from counts the DATABASE already computed.
 *
 *  The hydrated SPA no longer holds rows to count, so it reads the search
 *  endpoint's facet counts instead. Same floor, same lowercase merge, same
 *  stable order — because the hub-of-hubs must list exactly the hubs the
 *  worker's `/recipes/platform/<value>` route will serve, and a tile the route
 *  404s is worse than a missing tile.
 *
 *  ⚠ One asymmetry, named rather than hidden: the row-counting version dedupes
 *  PER ROW before counting, so one recipe tagged both `HubSpot` and `hubspot`
 *  counts once; merging pre-computed counts here would count it twice. It cannot
 *  happen today — every platform value in the catalogue is already lowercase,
 *  which is why the route parser lowercases too — and if it ever did, the effect
 *  is a hub sized slightly high, not a broken link. */
export const qualifyingFacetsFromCounts = (
  counts: ReadonlyArray<FacetCount>,
): FacetCount[] => {
  const merged = new Map<string, number>();
  for (const entry of counts) {
    if (typeof entry?.value !== 'string' || entry.value === '') continue;
    const key = entry.value.toLowerCase();
    merged.set(key, (merged.get(key) ?? 0) + entry.count);
  }
  return [...merged.entries()]
    .filter(([, n]) => n >= MIN_FACET_LISTINGS)
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
};

/** A facet hub's masthead. The <h1> carries the subject — this is the page's
 *  whole reason to exist as a separate URL, and a crawler reads it as the
 *  topic. The crumb back to /recipes keeps the hub inside the site's shape
 *  rather than dangling off it. */
const renderFacetHero = (state: MarketplaceState): string => {
  const label = facetLabel(state.facet?.value ?? '');
  // `totals.recipes` on a hub route is the FACET's count, not the catalogue's —
  // both producers set it that way (`fetchFacetPage`, and the SPA's hub load).
  // Without it the rows in hand are all there is to count.
  const n = state.totals?.recipes ?? state.recipes.length;
  return `
    <section class="mp-hero mp-facet-hero">
      <a href="/recipes" class="mp-back">&larr; All recipes</a>
      <span class="mp-eyebrow"><a href="${e(facetIndexPath(state.facet?.kind ?? 'platform'))}">Platform</a></span>
      <h1>${e(label)} recipes</h1>
      <p>${n} ${n === 1 ? 'recipe works' : 'recipes work'} with ${e(label)} in Recued — inspect what each one does, what it installs, and what it can access before bringing it in.</p>
    </section>
  `;
};

/** The hub-of-hubs — `/recipes/platform`.
 *
 *  Twenty hub pages existed with no way to reach them: the sitemap listed them
 *  and 2,624 detail pages linked into them, but nothing linked ACROSS them, so
 *  a reader could only arrive at one by already being on a recipe that used it.
 *  This is that index.
 *
 *  Every count here is corpus-wide (see `MarketplaceState.facetIndex`), and the
 *  list is exactly what the route will serve — same `qualifyingFacets`, same
 *  floor — so no card on this page can lead to a 404 or a redirect. */
const renderFacetIndex = (state: MarketplaceState): string => {
  const kind = state.facetIndex?.kind ?? 'platform';
  // Server counts win. Absent ⇒ the rows in hand are the whole catalogue (the
  // hydrated SPA), so the SAME function that decided the server's list decides
  // this one — the page cannot change shape under the reader on hydration.
  const values = state.facetIndex?.values ?? qualifyingFacets(state.recipes, kind);
  // No corpus-wide sum here, deliberately: adding the tile counts up gives the
  // total SIZE OF THE HUBS, which is not a number of recipes — a recipe on two
  // platforms lands in both — and next to a heading about recipes it would read
  // as one. Each tile's own count is exact; the page needs nothing above them.
  return `
    <section class="mp-hero mp-facet-hero">
      <a href="/recipes" class="mp-back">&larr; All recipes</a>
      <span class="mp-eyebrow">Browse</span>
      <h1>Recipes by platform</h1>
      <p>${values.length} ${values.length === 1 ? 'platform has' : 'platforms have'} a page here — every platform carrying ${MIN_FACET_LISTINGS} recipes or more. The rest are in the <a href="/recipes">full catalogue</a>, which the search box filters by platform name.</p>
    </section>
    ${values.length === 0
      ? '<div class="mp-empty">No platform has enough recipes for its own page yet.</div>'
      : `<div class="mp-facet-grid">
      ${values.map((f) => `
        <a class="mp-facet-tile" href="${e(facetPath(kind, f.value))}">
          <strong>${e(facetLabel(f.value))}</strong>
          <span>${f.count} ${f.count === 1 ? 'recipe' : 'recipes'}</span>
        </a>
      `).join('')}
    </div>`}
  `;
};

const renderSearch = (state: MarketplaceState): string => `
  <div class="mp-search">
    <input
      type="text"
      data-field="search"
      value="${e(state.search)}"
      aria-label="Search the marketplace"
      placeholder="Search ${state.view === 'landing' ? 'recipes and packs' : state.view === 'packs' ? 'packs' : 'recipes'}…"
    />
  </div>
`;

const SORT_LABELS: Record<SortKey, string> = {
  popular: 'Popular',
  newest: 'Newest',
  updated: 'Updated',
  name: 'A–Z',
};

/** Shared sort control — recipe + pack filter bars pass their own ordered
 *  `SortKey` list (both use the full set today). */
const renderSortGroup = (state: MarketplaceState, sorts: readonly SortKey[]): string => `
  <div class="mp-filter-group mp-sort-group">
    <span class="mp-filter-label">Sort</span>
    <div class="mp-filters">
      ${sorts.map((s) => `
        <button class="mp-filter ${state.sort === s ? 'active' : ''}"
          data-action="sort" data-value="${s}">${SORT_LABELS[s]}</button>
      `).join('')}
    </div>
  </div>
`;

/** Generic chip-group: an "All" reset + one chip per value. `selected` marks the
 *  active chip; `label` maps a raw value to its display text (identity default). */
const renderChipGroup = (
  title: string,
  action: string,
  values: readonly string[],
  selected: string | null,
  label: (v: string) => string = (v) => v,
): string => `
  <div class="mp-filter-group">
    <span class="mp-filter-label">${e(title)}</span>
    <div class="mp-filters">
      <button class="mp-filter ${!selected ? 'active' : ''}"
        data-action="${e(action)}" data-value="">All</button>
      ${values.map((v) => `
        <button class="mp-filter ${selected === v ? 'active' : ''}"
          data-action="${e(action)}" data-value="${e(v)}">${e(label(v))}</button>
      `).join('')}
    </div>
  </div>
`;

/** Chip values for one facet group.
 *
 *  `state.facets` — the endpoint's drop-one-out counts over the WHOLE matching
 *  set — wins wherever present. The row-counting fallback is what the SSR
 *  worker renders with: it has a page of rows and no facet response, so its
 *  chips are a sample of the catalogue's facets. That was true of the SSR
 *  before this change and stays true; what changed is that the hydrated SPA now
 *  replaces them with real counts instead of with a whole-corpus scan.
 *
 *  Capped either way — the endpoint's arrays are already count-desc, so the
 *  slice reproduces the same top-N the local counters produced. */
const facetChips = (
  state: MarketplaceState,
  key: string,
  fallback: () => string[],
): string[] => {
  const served = state.facets?.[key];
  if (served === undefined) return fallback();
  return served.slice(0, FACET_CHIP_LIMIT).map((f) => f.value);
};

const renderRecipeFilters = (state: MarketplaceState): string => {
  const platforms = facetChips(state, 'platform', () => extractPlatforms(state.recipes));
  const tags = facetChips(state, 'tag', () => extractTags(state.recipes));
  // ⚠ Capped like the others once the server counts them. The handle vocabulary
  // is unbounded and grows with every publisher who signs up, so an uncapped
  // group becomes a wall of chips — the reason the webclient's Discover exposes
  // no publisher group at all. Ordered most-published first rather than
  // alphabetically, so the cap keeps the publishers a reader is likeliest to
  // want.
  const publishers = facetChips(state, 'publisher', () =>
    [...new Set(state.recipes.map((r) => r.publisher_id))]
      .sort((a, b) => publisherDisplayName(state, a).localeCompare(publisherDisplayName(state, b))));
  const hasActiveFilter =
    state.platformFilter || state.tagFilter || state.publisherFilter;

  return `
    <div class="mp-filter-bar">
      ${platforms.length > 0 ? renderChipGroup('Platform', 'filter-platform', platforms, state.platformFilter) : ''}
      ${tags.length > 0 ? renderChipGroup('Tags', 'filter-tag', tags, state.tagFilter) : ''}
      ${publishers.length > 1 ? renderChipGroup('Publisher', 'filter-publisher', publishers, state.publisherFilter, (h) => publisherDisplayName(state, h)) : ''}
      ${renderSortGroup(state, ['popular', 'newest', 'updated', 'name'])}
      ${hasActiveFilter ? `
        <button class="mp-clear-filters" data-action="clear-filters">Clear filters</button>
      ` : ''}
    </div>
  `;
};

/** The platform facet's chips.
 *
 *  ⚠ CAPPED, and by FREQUENCY — the same rule tags have always had, for a
 *  stronger reason. `platform` is one slug per integration (2,414 of 2,624
 *  recipes carry exactly one), so the vocabulary is 166 values and grows with
 *  every generator shipped. Listing them all alphabetically turned the filter
 *  bar into a wall of chips that gets worse on every release, and buried the
 *  dozen anyone actually filters by under the ones they don't. Rare platforms
 *  are reachable through the search box, which handles them fine. */
const extractPlatforms = (recipes: RecipeListing[]): string[] => {
  const counts = new Map<string, number>();
  for (const r of recipes) {
    // A recipe listing a platform twice still counts once toward it.
    for (const p of new Set(r.platforms ?? [])) counts.set(p, (counts.get(p) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, FACET_CHIP_LIMIT)
    .map(([platform]) => platform);
};

/** How many chips a facet group shows. Shared by platforms and tags so the two
 *  bars stay the same shape. */
export const FACET_CHIP_LIMIT = 12;

const extractTags = (items: Array<{ tags?: string[] }>): string[] => {
  const counts = new Map<string, number>();
  for (const item of items) {
    for (const t of item.tags ?? []) {
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
  }
  // Sorted by frequency (most used first), capped — see FACET_CHIP_LIMIT.
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, FACET_CHIP_LIMIT)
    .map(([tag]) => tag);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const ingredientKindForListing = (ingredient: IngredientListing): MarketplaceIngredientKindFilter | null => {
  const kind = marketplaceIngredientKind({
    manifest: ingredient.manifest as MarketplaceIngredientRenderState['manifest'],
  });
  return kind;
};

// ── Stars + stats helpers ──

const renderStars = (avg: number, count: number): string => {
  if (count === 0) return '';
  const full = Math.floor(avg);
  const half = avg - full >= 0.25 && avg - full < 0.75 ? 1 : 0;
  const empty = 5 - full - half;
  const stars = '&#9733;'.repeat(full) + (half ? '&#9734;' : '') + '&#9734;'.repeat(empty - (half ? 0 : 0));
  return `<span class="mp-stars" title="${avg.toFixed(1)} / 5 (${count} rating${count === 1 ? '' : 's'})">${stars} <span class="mp-rating-num">${avg.toFixed(1)}</span> <span class="mp-rating-count">(${count})</span></span>`;
};

const renderMarketplaceStats = (state: MarketplaceState): string => {
  // MP-4 — public stats cover the two PUBLIC content types (recipes + packs);
  // ingredients are an internal unit (no public page) so they never appear here.
  //
  // `state.totals` wins where present: on a server-paged render the state holds
  // 48 rows, and counting them would publish "48 recipes" on the marketplace's
  // own stats bar.
  const totalRecipes = state.totals?.recipes ?? state.recipes.length;
  const totalPacks = state.totals?.packs ?? state.packs.length;
  const publishers = state.totals?.publishers ?? new Set([
    ...state.recipes.map((r) => r.publisher_id),
    ...state.packs.map((p) => p.publisher_id),
  ]).size;

  // No marketplace-wide installs total (owner, 2026-07-21). A single number
  // summed across every listing says nothing useful about any of them, and it
  // is the one stat here that is not a count of rows we can see. Popularity now
  // lives per-listing, on the cards and the detail header, where it is about a
  // thing the reader is actually looking at.
  return `
    <div class="mp-stats">
      <span class="mp-stat"><strong>${totalRecipes}</strong> recipes</span>
      <span class="mp-stat"><strong>${totalPacks}</strong> packs</span>
      <span class="mp-stat"><strong>${publishers}</strong> publishers</span>
    </div>
  `;
};

/** "1 view" / "2 views". The webclient's discover meta has pluralised since it
 *  shipped; this template never did, so the first real counts rendered as
 *  "1 views". Grammar is part of the claim a surface makes. */
export const countLabel = (n: number, noun: string): string =>
  `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`;

/** Detail-header counts. Views and installs are SEPARATE chips, each omitted at
 *  zero — "no chip" reads as "no data yet", whereas "0 installs" on a live
 *  listing reads as "nobody wanted this", which for anything published before
 *  the counter existed is simply false.
 *
 *  `view_count` is optional: rows read before migration 026, and the webclient's
 *  roster projection, have no such field. `?? 0` would render those as a
 *  confident zero, so absence and zero are both treated as "say nothing". */
export const renderListingCounts = (views: number | undefined, installs: number | undefined): string => {
  const parts: string[] = [];
  if (typeof views === 'number' && views > 0) parts.push(`<span>${countLabel(views, 'view')}</span>`);
  if (typeof installs === 'number' && installs > 0) parts.push(`<span>${countLabel(installs, 'install')}</span>`);
  return parts.join('\n          ');
};

// ── Recipe card ──

/** Pill shown next to publisher names when `publishers.certified` is
 *  true.  Small, neutral colour — not a brag, more of a trust hint. */
const renderCertifiedPill = (certified: boolean | undefined): string =>
  certified
    ? '<span class="mp-certified-pill" title="Certified author">Certified</span>'
    : '';

/** D-120 Phase 3 — pill shown when a recipe declares
 *  `provenance: false`. Default emission is on (engine writes
 *  causal entity↔memory link rows for every step touch); the
 *  badge surfaces the opt-out so users can see the recipe declines
 *  tracing before installing. Recipes that omit the field render
 *  nothing — the default-on case is the silent path. */
/** The "No provenance" badge, from whichever source the caller has.
 *
 *  ⚠ Takes the LISTING, not the body. The lean browse corpus drops `recipe`
 *  but carries the decision as `provenance_opted_out`, so a card renders the
 *  badge identically either way. Reading the body alone would silently drop a
 *  trust signal from every hydrated card — the badge would be on the
 *  server-rendered page and gone a moment later. */
const renderProvenanceOptOutBadge = (
  listing: { provenance_opted_out?: boolean; recipe?: { provenance?: unknown } },
): string =>
  (listing.provenance_opted_out ?? listing.recipe?.provenance === false)
    ? '<span class="mp-provenance-optout-badge" title="This recipe opts out of provenance tracking — installs will not record causal entity↔memory links.">No provenance</span>'
    : '';

const renderRecipeCard = (
  r: RecipeListing,
  state?: Pick<MarketplaceState, 'publishers'>,
): string => {
  const platforms = r.platforms ?? [];
  const tags = r.tags ?? [];
  const publisher = state
    ? renderPublisherLink(state, r.publisher_id)
    : `<a href="${publisherHref(r.publisher_id)}" class="mp-publisher-link">${e(r.publisher_id)}</a>`;
  return `
    <article class="mp-card mp-recipe-card">
      <a href="/recipes/${e(r.recipe_id)}" class="mp-card-name">${e(r.name)}</a>
      <p class="mp-card-desc">${e(r.description ?? '')}</p>
      <div class="mp-card-tags">
        ${platforms.map((p) =>
          `<button class="mp-tag mp-tag-clickable" data-action="filter-platform" data-value="${e(p)}">${e(p)}</button>`,
        ).join('')}
        ${tags.slice(0, 4).map((t) =>
          `<button class="mp-tag mp-tag-clickable" data-action="filter-tag" data-value="${e(t)}">${e(t)}</button>`,
        ).join('')}
      </div>
      <div class="mp-card-meta">
        <span>by ${publisher}${renderCertifiedPill(r.publisher_certified)}</span>
        <span>v${e(String(r.version))}</span>
        ${r.download_count > 0 ? `<span>${countLabel(r.download_count, 'install')}</span>` : ''}
        ${renderStars(r.rating_avg ?? 0, r.rating_count ?? 0)}
        ${renderProvenanceOptOutBadge(r)}
      </div>
      <div class="mp-card-actions">
        <a href="/recipes/${e(r.recipe_id)}" class="mp-install-btn">View &rarr;</a>
      </div>
    </article>
  `;
};

// ── Step & Data Visualizer ──

interface StepInfo {
  id: string;
  type: 'prefetch' | 'transform' | 'ingredient' | 'guard' | 'ai';
  label: string;
  refs: string[];
  skipWhen?: string;
  failOn?: string;
  optional?: boolean;
}

/** Extract {{step.xxx}} and {{context.xxx}} references from any value. */
const extractRefs = (obj: unknown): string[] => {
  const refs: string[] = [];
  const str = JSON.stringify(obj ?? '');
  const re = /\{\{(step\.[a-z_][a-z0-9_.]*|context\.[a-z_][a-z0-9_.]*|config\.[a-z_][a-z0-9_.]*|vault\.[a-z_][a-z0-9_.]*)\}\}/gi;
  let m;
  while ((m = re.exec(str)) !== null) refs.push(m[1]);
  return [...new Set(refs)];
};

/** The AI function a step runs (`ai-summarize`), or undefined. An op step names
 *  what it calls in `op` — `core.ai.summarize`, `recued-core.recurly.account.read` —
 *  which is how every shipped recipe writes its calls; an ingredient step in its slug
 *  (§5 — a `core-ai-*` alias is an AI step too). */
const aiFunctionOfStep = (step: Record<string, unknown>): string | undefined => {
  if (typeof step.op === 'string') {
    const parsed = parseOpId(step.op);
    return parsed?.tier === 'kernel' && parsed.domain === 'ai' ? `ai-${parsed.op}` : undefined;
  }
  if (typeof step.ingredient === 'string') {
    const fn = stripCorePrefix(step.ingredient);
    return fn.startsWith('ai-') ? fn : undefined;
  }
  return undefined;
};

const classifyStep = (step: Record<string, unknown>): StepInfo => {
  const id = String(step.id ?? '?');
  const refs = extractRefs(step);
  const skipWhen = step.skip_when ? String(step.skip_when) : undefined;
  const failOn = step.fail_on ? String(step.fail_on) : undefined;
  const optional = step.optional === true;

  if (step.guard) return { id, type: 'guard', label: 'guard', refs, skipWhen, failOn, optional };
  // ⛔ An op step fell through to the last line below: a transform labelled `?`,
  // with no description — every step of nearly every shipped recipe.
  if (typeof step.op === 'string') {
    const type = aiFunctionOfStep(step) !== undefined ? 'ai' : 'ingredient';
    return { id, type, label: step.op, refs, skipWhen, failOn, optional };
  }
  if (step.ingredient) {
    const slug = String(step.ingredient);
    const isAi = aiFunctionOfStep(step) !== undefined;
    return { id, type: isAi ? 'ai' : 'ingredient', label: slug, refs, skipWhen, failOn, optional };
  }
  if (step.transform) return { id, type: 'transform', label: String(step.transform), refs, skipWhen, failOn, optional };
  return { id, type: 'transform', label: '?', refs, skipWhen, failOn, optional };
};

// ── Plain English step descriptions ──

/** Humanize a reference like "step.deal.last_activity_date" → "deal's last activity date" */
const humanRef = (ref: string): string => {
  // Strip namespace prefix
  const stripped = ref
    .replace(/^step\./, '')
    .replace(/^context\./, '')
    .replace(/^config\./, 'setting ')
    .replace(/^vault\./, 'credential ');
  // Convert snake_case/dot paths to readable
  return stripped.replace(/[._]/g, ' ').replace(/\s+/g, ' ').trim();
};

/** The longest object a page prints as JSON; a longer one is cut with "…" (some
 *  object defaults are tables several KB long). Text and lists print in full. */
const OBJECT_JSON_MAX = 80;

/** A literal object as one line of JSON, cut at {@link OBJECT_JSON_MAX}. */
const objectJson = (value: object): string => {
  const chars = Array.from(JSON.stringify(value));
  return chars.length > OBJECT_JSON_MAX ? `${chars.slice(0, OBJECT_JSON_MAX - 1).join('')}…` : chars.join('');
};

/** Humanize a {{ref}} string, stripping mustaches. A literal list reads as its
 *  items and a literal object as JSON. ⛔ Never `String(value)` on an object: a
 *  coalesce falling back to `{}` read "[object Object]". */
const humanValue = (v: unknown): string => {
  if (Array.isArray(v)) return v.map(humanValue).join(', ');
  if (v !== null && typeof v === 'object') return objectJson(v);
  const s = String(v ?? '');
  const m = s.match(/^\{\{(.+)\}\}$/);
  if (m) return humanRef(m[1]);
  return s;
};

const OP_WORDS: Record<string, string> = {
  equal: 'equals', not_equal: 'does not equal',
  greater: 'is greater than', greater_or_equal: 'is at least',
  less: 'is less than', less_or_equal: 'is at most',
  is_null: 'is missing', is_not_null: 'exists',
  is_empty: 'is empty', is_not_empty: 'has data',
  contains: 'contains', not_contains: 'does not contain',
  in: 'is one of', not_in: 'is not one of',
};

const MATH_OPS: Record<string, string> = {
  add: 'plus', subtract: 'minus', multiply: 'times', divide: 'divided by',
};

/** What each AI function does, for a step that runs it in either form. */
const AI_STEP_DESCRIPTIONS: Readonly<Record<string, string>> = {
  'ai-classify': 'AI classifies data into one of the given categories',
  'ai-score': 'AI scores data against defined criteria',
  'ai-extract': 'AI extracts structured fields from data',
  'ai-summarize': 'AI generates a concise summary',
  'ai-sentiment': 'AI analyzes sentiment and signals',
  'ai-compare': 'AI compares two pieces of data',
  'ai-generate': 'AI generates content from a template',
  'ai-translate': 'AI translates text to the target language',
  'ai-rewrite': 'AI rewrites text in the specified style',
  'ai-prompt': 'AI generates a response from a custom prompt',
  'ai-embed': 'AI turns text into embeddings for similarity search',
};

/** A plain sentence for an op that is not an AI function, from its id: the last
 *  segment is the verb, the rest what it acts on, and a pack op names its pack —
 *  `recued-core.recurly.account.read` → "Read account on recurly",
 *  `core.notification.send` → "Send notification", `deal.read` → "Read deal". */
const describeOp = (op: string): string => {
  const parsed = parseOpId(op);
  const segments = parsed === null
    ? op.split('.')
    : parsed.tier === 'kernel'
      ? [parsed.domain, ...parsed.op.split('.')]
      : parsed.operation.split('.');
  const words = (s: string): string => s.replace(/[-_]+/g, ' ');
  const verb = words(segments[segments.length - 1] ?? '');
  const object = segments.slice(0, -1).map(words).join(' ');
  const sentence = `${verb.charAt(0).toUpperCase()}${verb.slice(1)}${object ? ` ${object}` : ''}`;
  return parsed?.tier === 'pack' ? `${sentence} on ${words(parsed.pack)}` : sentence;
};

/** Generate a plain English sentence for a recipe step. */
const describeStep = (step: Record<string, unknown>): string => {
  // Guard
  if (step.guard) {
    const g = String(step.guard);
    const parts = g.replace(/\{\{|\}\}/g, '').split(/\s+/);
    const field = humanRef(parts[0]);
    const op = OP_WORDS[parts[1]] ?? parts[1] ?? '';
    return `Stop if ${field} ${op}`;
  }

  // Ingredient or op step (prefetch or sequential)
  if (step.ingredient || typeof step.op === 'string') {
    const pii = Array.isArray(step.pii_fields) && (step.pii_fields as string[]).length > 0;
    const piiNote = pii ? ' (PII auto-protected)' : '';
    const fn = aiFunctionOfStep(step);
    if (fn !== undefined && AI_STEP_DESCRIPTIONS[fn] !== undefined) return `${AI_STEP_DESCRIPTIONS[fn]}${piiNote}`;
    if (typeof step.op === 'string') return describeOp(step.op);
    const slug = String(step.ingredient);
    // Data ingredients — parse the slug pattern
    const parts = slug.split('-');
    const platform = parts[parts.length - 1];
    const action = parts[0];
    const entity = parts.slice(1, -1).join(' ');
    if (action === 'search') return `Search ${entity} on ${platform}`;
    return `Fetch ${entity} from ${platform}`;
  }

  // Transforms
  const transform = String(step.transform ?? '');
  switch (transform) {
    case 'coalesce': {
      const vals = (step.values as unknown[]) ?? [];
      const names = vals.map(humanValue);
      return `Use the first available value from: ${names.join(', ')}`;
    }
    case 'date_diff': {
      const unit = String(step.unit ?? 'days');
      return `Calculate ${unit} between ${humanValue(step.from)} and ${humanValue(step.to)}`;
    }
    case 'date_format':
      return `Format ${humanValue(step.input ?? step.value)} as a date`;
    case 'date_add':
      return `Add ${humanValue(step.amount)} ${String(step.unit ?? 'days')} to ${humanValue(step.input ?? step.from)}`;
    case 'date_parse':
      return `Parse ${humanValue(step.input ?? step.value)} as a date`;
    case 'is_past':
      return `Check if ${humanValue(step.input ?? step.value)} is in the past`;
    case 'is_future':
      return `Check if ${humanValue(step.input ?? step.value)} is in the future`;
    case 'math': {
      if (step.expression) return `Calculate: ${String(step.expression)}`;
      const op = MATH_OPS[String(step.operator ?? '')] ?? String(step.operator ?? '');
      return `Calculate ${humanValue(step.left)} ${op} ${humanValue(step.right)}`;
    }
    case 'weighted_score': {
      const scores = (step.scores ?? []) as Array<{ value: unknown; weight: unknown }>;
      const parts = scores.map((s) => `${humanValue(s.value)} (${Number(s.weight) * 100}%)`);
      return `Weighted score from: ${parts.join(', ')}`;
    }
    case 'compare': {
      const op = OP_WORDS[String(step.operator ?? '')] ?? String(step.operator ?? '');
      return `Check if ${humanValue(step.left)} ${op} ${humanValue(step.right)}`;
    }
    case 'filter':
      return `Filter ${humanValue(step.input)} by condition`;
    case 'sort':
      return `Sort ${humanValue(step.input)} by ${humanValue(step.key ?? step.field)}`;
    case 'map':
      return `Extract ${humanValue(step.field ?? step.path)} from each item`;
    case 'reduce':
      return `Reduce ${humanValue(step.input)} with ${String(step.operator ?? 'accumulator')}`;
    case 'unique':
      return `Remove duplicates from ${humanValue(step.input)}`;
    case 'flatten':
      return `Flatten nested lists in ${humanValue(step.input)}`;
    case 'slice':
      return `Take a subset of ${humanValue(step.input)}`;
    case 'group_by':
      return `Group ${humanValue(step.input)} by ${humanValue(step.key ?? step.field)}`;
    case 'merge':
      return 'Combine objects together';
    case 'pick':
      return `Keep only specific fields from ${humanValue(step.input ?? step.data)}`;
    case 'omit':
      return `Remove specific fields from ${humanValue(step.input ?? step.data)}`;
    case 'set':
      return `Set ${humanValue(step.path ?? step.field)} to ${humanValue(step.value)}`;
    case 'rename':
      return `Rename fields in ${humanValue(step.input ?? step.data)}`;
    case 'concat':
      return 'Combine text or lists together';
    case 'template':
      return 'Generate text from a template';
    case 'replace':
      return `Replace text in ${humanValue(step.input ?? step.data)}`;
    case 'split':
      return `Split ${humanValue(step.input ?? step.data)} into parts`;
    case 'lowercase':
      return `Convert ${humanValue(step.input ?? step.data)} to lowercase`;
    case 'uppercase':
      return `Convert ${humanValue(step.input ?? step.data)} to uppercase`;
    case 'trim':
      return `Trim whitespace from ${humanValue(step.input ?? step.data)}`;
    case 'truncate':
      return `Truncate ${humanValue(step.input ?? step.data)} to a max length`;
    case 'round':
      return `Round ${humanValue(step.input ?? step.value)} to ${step.precision ?? 0} decimal places`;
    case 'clamp':
      return `Clamp ${humanValue(step.input ?? step.value)} between ${step.min ?? '?'} and ${step.max ?? '?'}`;
    case 'to_number':
      return `Convert ${humanValue(step.input ?? step.value)} to a number`;
    case 'switch': {
      const keys = Object.keys(step.cases as Record<string, unknown> ?? {});
      return `Map ${humanValue(step.input)} to one of: ${keys.join(', ')}`;
    }
    case 'any':
      return 'Check if any of the conditions are true';
    case 'all':
      return 'Check if all conditions are true';
    case 'count':
      return `Count items in ${humanValue(step.input ?? step.data)}`;
    case 'find':
      return `Find first match in ${humanValue(step.input)}`;
    case 'pluck':
      return `Extract ${humanValue(step.field)} from each item`;
    case 'sum':
      return `Sum values in ${humanValue(step.input)}`;
    case 'min_by':
    case 'max_by':
      return `Find the ${transform === 'min_by' ? 'minimum' : 'maximum'} by ${humanValue(step.field ?? step.key)}`;
    case 'percent':
      return `Calculate percentage: ${humanValue(step.value)} of ${humanValue(step.total)}`;
    case 'join':
      return `Join items with "${String(step.separator ?? ', ')}"`;
    case 'hash_replace':
      return `Anonymize sensitive fields for safe AI processing`;
    case 'hash_restore':
      return 'Restore anonymized fields back to real values';
    case 'redact':
      return `Redact sensitive content in ${humanValue(step.input ?? step.data)}`;
    case 'to_checklist':
      return 'Format results as an actionable checklist';
    case 'to_table':
      return 'Format results as a data table';
    case 'to_summary':
      return 'Format results as a summary card';
    case 'to_csv':
      return 'Serialize rows as a CSV string';
    case 'starts_with':
      return `Check if ${humanValue(step.input ?? step.value)} starts with ${humanValue(step.prefix ?? step.search)}`;
    case 'ends_with':
      return `Check if ${humanValue(step.input ?? step.value)} ends with ${humanValue(step.suffix ?? step.search)}`;
    default:
      return transform ? `Apply ${transform}` : '';
  }
};

const STEP_ICONS: Record<StepInfo['type'], string> = {
  prefetch: '&#x25BC;',  // ▼ fetch
  transform: '&#x25A0;', // ■ transform
  ingredient: '&#x25C6;',// ◆ ingredient
  guard: '&#x26A0;',     // ⚠ guard
  ai: '&#x2726;',        // ✦ ai
};

const renderStepNode = (s: StepInfo, raw: Record<string, unknown>): string => {
  const icon = STEP_ICONS[s.type];
  const desc = describeStep(raw);

  return `
    <div class="viz-step viz-${s.type}${s.optional ? ' viz-optional' : ''}">
      <div class="viz-step-header">
        <span class="viz-icon">${icon}</span>
        <span class="viz-id">${e(s.id)}</span>
        <span class="viz-type-badge">${e(s.label)}</span>
      </div>
      ${desc ? `<div class="viz-desc">${e(desc)}</div>` : ''}
      ${s.skipWhen ? `<div class="viz-cond viz-skip">skip: ${e(s.skipWhen.replace(/\{\{|\}\}/g, ''))}</div>` : ''}
      ${s.failOn ? `<div class="viz-cond viz-fail">fail: ${e(s.failOn.replace(/\{\{|\}\}/g, ''))}</div>` : ''}
    </div>
  `;
};

const renderRecipeVisualizer = (recipe: RecipeListing['recipe']): string => {
  if (!recipe) return '';
  const prefetch = (recipe.prefetch_steps ?? []) as Record<string, unknown>[];
  const steps = (recipe.steps ?? []) as Record<string, unknown>[];
  if (prefetch.length === 0 && steps.length === 0) return '';

  const prefetchInfos = prefetch.map((s) => ({ info: { ...classifyStep(s), type: 'prefetch' as const }, raw: s }));
  const stepInfos = steps.map((s) => ({ info: classifyStep(s), raw: s }));

  // Output section. ⛔ This read `output.sidebar` alone; `render` is the canonical key
  // (D-195, `sidebar` the legacy alias) and no shipped recipe writes `sidebar`
  // (2,368 of 2,400 write `render`), so the Output phase was empty on every page.
  const outputItems = recipeOutputSections(recipe as Parameters<typeof recipeOutputSections>[0])
    .map((section) => {
      const { type, source } = section as { type?: unknown; source?: unknown };
      return { type: String(type ?? ''), source: typeof source === 'string' ? source : '' };
    });
  const stageCount = prefetchInfos.length + stepInfos.length;

  return `
    <details class="mp-detail-section mp-disclosure">
      <summary>
        <span>How it works</span>
        <span class="mp-disclosure-meta">${stageCount} step${stageCount === 1 ? '' : 's'}</span>
      </summary>
      <div class="mp-disclosure-body">
        <p class="mp-disclosure-intro">Inspect the data fetches, transforms, gates, and output this recipe runs.</p>
        <div class="viz-pipeline">
        ${prefetchInfos.length > 0 ? `
          <div class="viz-phase">
            <div class="viz-phase-label">Data fetch</div>
            <div class="viz-steps">
              ${prefetchInfos.map(({ info, raw }) => renderStepNode(info, raw)).join('<div class="viz-connector"></div>')}
            </div>
          </div>
          <div class="viz-phase-arrow">&#x25BC;</div>
        ` : ''}
        <div class="viz-phase">
          <div class="viz-phase-label">Process (${stepInfos.length} steps)</div>
          <div class="viz-steps">
            ${stepInfos.map(({ info, raw }) => renderStepNode(info, raw)).join('<div class="viz-connector"></div>')}
          </div>
        </div>
        ${outputItems.length > 0 ? `
          <div class="viz-phase-arrow">&#x25BC;</div>
          <div class="viz-phase">
            <div class="viz-phase-label">Output</div>
            <div class="viz-outputs">
              ${outputItems.map((o) => `
                <div class="viz-output">
                  <span class="viz-type-badge">${e(o.type)}</span>
                  <span class="viz-ref">${e(o.source.replace('step.', ''))}</span>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}
        </div>
      </div>
    </details>
  `;
};

// ── Variables disclosure ──

const describeVarType = (value: unknown): string => {
  if (typeof value === 'boolean') return 'toggle';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'choice';
  if (value === null) return 'required';
  if (typeof value === 'object' && value !== null && 'label' in value) return 'setting';
  return 'text';
};

/** One setting value as words. ⛔ Never `String(value)` on an object: that printed
 *  "[object Object]" for every object default. An empty value is "—". */
const describeSettingValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length > 0 ? value.map(describeSettingValue).join(', ') : '—';
  if (typeof value === 'object') return Object.keys(value).length > 0 ? objectJson(value) : '—';
  return String(value);
};

/** What a settings row shows after "=": the setting's default, the way a run
 *  resolves it. A setting is a bare default or a ValueHint (`VariableDefault`).
 *  A hint with no `default` has no value until the owner gives one: preflight asks
 *  for it unless it is `optional` (`findMissingVariables`), an enum hint included. */
const describeVarDefault = (value: unknown): string => {
  if (value === null) return 'set at install';
  // The bare list form is a choice: its first item is the default.
  if (Array.isArray(value)) return value.length > 0 ? describeSettingValue(value[0]) : '—';
  if (typeof value === 'object') {
    const hint = value as { default?: unknown; optional?: unknown };
    if (hint.default !== undefined) return describeSettingValue(hint.default);
    return hint.optional === true ? 'optional' : 'set at install';
  }
  return describeSettingValue(value);
};

const renderVariablesSection = (variables: Record<string, unknown>): string => {
  const entries = Object.entries(variables);
  if (entries.length === 0) return '';

  return `
    <details class="mp-detail-section mp-disclosure">
      <summary>
        <span>Settings</span>
        <span class="mp-disclosure-meta">${entries.length} configurable</span>
      </summary>
      <div class="mp-disclosure-body">
        <p class="mp-disclosure-intro">Configurable at install. Defaults shown — change them anytime in Recued.</p>
        <div class="viz-io-list">
        ${entries.map(([key, value]) => {
          const type = describeVarType(value);
          const dflt = describeVarDefault(value);
          const humanKey = key.replace(/_/g, ' ');
          const choices = Array.isArray(value) ? value.map((v) => e(describeSettingValue(v))).join(', ') : '';

          return `
            <div class="viz-io-item viz-input">
              <code>${e(humanKey)}</code>
              <span class="viz-type-badge">${e(type)}</span>
              <span class="viz-arrow">=</span>
              <span style="font-size: 12px; color: var(--fg);">${e(dflt)}</span>
              ${choices ? `<span style="font-size: 10px; color: var(--fg-muted);">(${choices})</span>` : ''}
            </div>
          `;
        }).join('')}
        </div>
      </div>
    </details>
  `;
};

// ── Recipe detail ──

// ────────────────────────────────────────────────────────────────
// 6c (treemap §12A) — recipe-detail conversion surface (MP-5).
// Trust & control + Uses + About + fork lineage + the sticky install
// panel. The trust surfaces introspect the referenced ingredient
// manifests (threaded in via D-a) to PREVIEW what installing the
// recipe would grant — informational only; actual grants happen at
// install (D-11). Every manifest is arbitrary stored JSONB, so the
// summary is built under one try/catch and degrades (omits a surface)
// rather than crashing the listing render.
// ────────────────────────────────────────────────────────────────

// D-203 — rank + labels are the canonical RISK_TIER_RANK / RISK_TIER_LABELS
// (contracts). The local wrapper keeps the marketplace `''`-for-unknown fallback
// (the canonical `riskTierLabel` title-cases unknowns instead).
const riskTierLabel = (risk: unknown): string =>
  isRiskTier(risk) ? RISK_TIER_LABELS[risk] : '';

const APPROVAL_INTENT_LABEL: Record<string, string> = {
  never: 'No approval', ask: 'Asks approval', always: 'Always asks',
};

/** Kinds that, at a write+ risk tier, mutate something OUTSIDE Recued (a
 *  connected account, a web API, a browser page, the local warehouse, a local
 *  service). `ai` / `chat` reason but never originate an external change.
 *  Generalises the D-145 action-with-conflict predicate to any write-tier
 *  kind so the preview flags every irreversible-by-default surface. */
const EXTERNAL_WRITE_KINDS = new Set(['http', 'mcp', 'connection', 'dom', 'storage', 'cli']);
const isExternalWriteManifest = (m: Record<string, unknown>): boolean =>
  typeof m.kind === 'string' && EXTERNAL_WRITE_KINDS.has(m.kind) &&
  typeof m.risk_tier === 'string' &&
  (m.risk_tier === 'write' || m.risk_tier === 'admin' || m.risk_tier === 'destructive');

/** Human label for the data-surface an ingredient kind touches. */
const KIND_DATA_LABEL: Record<string, string> = {
  http: 'Web APIs',
  connection: 'Connected accounts',
  mcp: 'MCP servers',
  dom: 'Browser pages',
  storage: 'Local warehouse',
  service: 'Local services',
  cli: 'Local CLI',
  ai: 'AI models',
  chat: 'AI chat',
};

interface RecipeGrant {
  name: string;
  kindLabel: string;
  risk: string;
  external: boolean;
  reversible: boolean;
  operations: { name: string; risk: string; approval: string; description: string }[];
}

/** One op a recipe calls — deduped by op id, in step order. Every shipped recipe
 *  writes its calls as `op:` steps, which name no ingredient. A kernel op
 *  (`core.ai.summarize`) carries what the registry says it does; a pack op
 *  (`recued-core.recurly.account.read`) is defined by its pack's composition. */
interface OpCall {
  op: string;
  kernel?: { risk: string; label: string; delivers: boolean };
}

/** What a loaded pack's composition declares for a pack op: `<publisher>.<pack>.
 *  <operation>` names the pack by publisher and by its slug or its composition's.
 *  A recipe page holds only its own bundle's pack (the install context), so an op
 *  from any other pack is not found here. Same identity guard as the bundle
 *  context: a manifest that names another pack is not read. */
const resolvePackOp = (
  op: string,
  packs: ReadonlyArray<PackListing>,
): { pack: PackListing; operation: string; risk: string; approval: string } | undefined => {
  const parsed = parseOpId(op);
  if (parsed?.tier !== 'pack') return undefined;
  for (const pack of packs) {
    const manifest = pack.manifest;
    if (manifest === undefined || pack.publisher_id !== parsed.publisher) continue;
    if (manifest.slug !== pack.slug || manifest.publisher !== pack.publisher_id) continue;
    for (const entry of manifest.contents ?? []) {
      const composition = (entry as { composition?: unknown }).composition;
      if (entry.type !== 'composition' || !isRecord(composition)) continue;
      if (pack.slug !== parsed.pack && composition.slug !== parsed.pack) continue;
      const operations = Array.isArray(composition.operations) ? composition.operations : [];
      const found = operations.find((o): o is Record<string, unknown> => isRecord(o) && o.op === parsed.operation);
      if (found !== undefined && isRiskTier(found.risk)) {
        return {
          pack,
          operation: parsed.operation,
          risk: found.risk,
          approval: typeof found.approval === 'string' ? found.approval : '',
        };
      }
    }
  }
  return undefined;
};

const opCallsOf = (recipe: RecipeListing['recipe']): OpCall[] => {
  const seen = new Set<string>();
  const out: OpCall[] = [];
  const triggerSteps = (recipe as { trigger_steps?: unknown } | undefined)?.trigger_steps;
  const lists: unknown[] = [triggerSteps, recipe?.prefetch_steps, recipe?.steps];
  for (const list of lists) {
    for (const step of Array.isArray(list) ? list : []) {
      const op = isRecord(step) && typeof step.op === 'string' ? step.op : undefined;
      if (op === undefined || seen.has(op)) continue;
      seen.add(op);
      const entry = getKernelOp(op);
      out.push(entry?.backing_slug !== undefined
        ? { op, kernel: { risk: entry.risk, label: OP_ENTITY_LABEL[entry.entity] ?? 'Built-in', delivers: kernelOpDelivers(op) } }
        : { op });
    }
  }
  return out;
};

interface RecipeTrustSummary {
  grants: RecipeGrant[];
  highestRisk: string;
  externalWrites: boolean;
  anyReversible: boolean;
  connections: string[];
  vaultScopes: string[];
  warehouseRefs: string[];
  /** Author-declared install permissions (`recipe.requires`), labelled. The
   *  authoritative, body-sourced grant set — recovered after ingredient
   *  manifests were retired from the cloud. */
  declaredPermissions: Array<{ label: string; code: string }>;
  /** A declared permission mutates an external surface — surfaced as the
   *  "Makes external changes" warning even with manifests retired. */
  declaredExternalWrites: boolean;
  unresolved: number;
}

const EMPTY_TRUST: RecipeTrustSummary = {
  grants: [], highestRisk: '', externalWrites: false,
  anyReversible: false, connections: [], vaultScopes: [], warehouseRefs: [],
  declaredPermissions: [], declaredExternalWrites: false, unresolved: 0,
};

const PERMISSION_LABELS: Record<string, string> = {
  read_memory: 'Read your memory',
  write_enrichment: 'Write enrichment data',
  read_enrichment: 'Read enrichment data',
  notification_send: 'Send notifications',
  read_contact: 'Read your contacts',
  read_calendar: 'Read your calendar',
  read_mail: 'Read your mail',
};

const VENDOR_LABELS: Record<string, string> = { hubspot: 'HubSpot', salesforce: 'Salesforce' };
// `Object.hasOwn`, NOT index/`in`: a published recipe's `requires` code is
// attacker-influenceable, and a bare lookup would resolve prototype keys
// (`toString`, `constructor`) to inherited functions.
const vendorLabel = (v: string): string =>
  Object.hasOwn(VENDOR_LABELS, v) ? VENDOR_LABELS[v] : (v.charAt(0).toUpperCase() + v.slice(1));

/** Human-readable label for a declared `requires` permission code. Returns null
 *  for the mechanical pack-install grant (not user-meaningful trust). */
const permissionLabel = (code: string): string | null => {
  if (code === 'install_bulk_pack') return null;
  if (Object.hasOwn(PERMISSION_LABELS, code)) return PERMISSION_LABELS[code];
  const read = /^read_connection_(.+)$/.exec(code);
  if (read) return `Read your ${vendorLabel(read[1])} connection`;
  const write = /^write_connection_(.+)$/.exec(code);
  if (write) return `Write to your ${vendorLabel(write[1])} connection`;
  return code.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
};

/** A declared permission that mutates an EXTERNAL surface (connected account /
 *  outbound send) — drives the "Makes external changes" warning post-manifest-
 *  retirement so the control plane never UNDER-warns. Local-warehouse writes
 *  (`write_enrichment`) are not external. Conservative: any `*_send` counts. */
const isExternalWritePermission = (code: string): boolean =>
  /^write_connection_/.test(code) || /_send$/.test(code);

/** Extract + label the recipe's declared `requires` permissions (deduped,
 *  install-grant filtered). Body-sourced + authoritative. */
const declaredPermissionsOf = (recipe: RecipeListing['recipe']): Array<{ label: string; code: string }> => {
  const raw = Array.isArray(recipe?.requires) ? recipe.requires : [];
  const seen = new Set<string>();
  const out: Array<{ label: string; code: string }> = [];
  for (const code of raw) {
    if (typeof code !== 'string' || seen.has(code)) continue;
    seen.add(code);
    const label = permissionLabel(code);
    if (typeof label === 'string' && label.length > 0) out.push({ label, code });
  }
  return out;
};

/** Distinct top-level warehouse namespaces (`data.contact`, `data.mail`, …)
 *  the recipe references — scanned out of the stringified recipe body. Best
 *  effort + capped; the warehouse chips are informational. */
const extractWarehouseRefs = (recipe: RecipeListing['recipe']): string[] => {
  const found = new Set<string>();
  const re = /\bdata\.([a-z][a-z0-9_]*)/gi;
  let match: RegExpExecArray | null;
  const json = JSON.stringify(recipe ?? {});
  while ((match = re.exec(json)) !== null) {
    found.add(`data.${match[1].toLowerCase()}`);
    if (found.size >= 8) break;
  }
  return [...found];
};

const summarizeRecipeTrust = (
  recipe: RecipeListing['recipe'],
  allIngredients: IngredientListing[],
  packs: ReadonlyArray<PackListing> = [],
): RecipeTrustSummary => {
  try {
    const declaredPermissions = declaredPermissionsOf(recipe);
    const slugs = extractIngredientSlugs(recipe);
    const bySlug = new Map(allIngredients.map((i) => [i.slug, i]));
    const grants: RecipeGrant[] = [];
    const connections = new Set<string>();
    const vaultScopes = new Set<string>();
    let unresolved = 0;
    // D-203 — init below the canonical 0-indexed floor (read = 0) so a
    // read-tier op still registers as "highest" (was 1-indexed with init 0).
    let highestRank = -1;
    let highestRisk = '';

    for (const slug of slugs) {
      const ing = bySlug.get(slug);
      if (!ing) { unresolved++; continue; }
      const m = ing.manifest as Record<string, unknown>;
      const kind = typeof m.kind === 'string' ? m.kind : '';
      const risk = typeof m.risk_tier === 'string' ? m.risk_tier : '';
      const operations: RecipeGrant['operations'] = [];
      if (isRecord(m.operations)) {
        for (const [key, spec] of Object.entries(m.operations)) {
          if (!isRecord(spec)) continue;
          operations.push({
            name: typeof spec.operation_id === 'string' ? spec.operation_id : key,
            risk: riskTierLabel(spec.risk_tier),
            approval: typeof spec.approval === 'string' && spec.approval in APPROVAL_INTENT_LABEL
              ? APPROVAL_INTENT_LABEL[spec.approval] : '',
            description: typeof spec.description === 'string' ? spec.description : '',
          });
        }
      }
      grants.push({
        name: ing.name || stripCorePrefix(slug),
        // Data-surface label ("Web APIs" / "Local CLI"), matching the pack
        // grant rows (computePackTrust) so the same ingredient reads the same
        // on recipe + pack detail — and so `cli` isn't the bare "Legacy" that
        // `marketplaceIngredientKindLabel` returns for it.
        kindLabel: (kind && kind in KIND_DATA_LABEL)
          ? KIND_DATA_LABEL[kind]
          : marketplaceIngredientKindLabel(ingredientKindForListing(ing)),
        risk: riskTierLabel(risk),
        external: isExternalWriteManifest(m),
        reversible: typeof m.cancellation_partner === 'string' && m.cancellation_partner.length > 0,
        operations,
      });
      if (kind && kind in KIND_DATA_LABEL) connections.add(KIND_DATA_LABEL[kind]);
      if (isRecord(m.vault_hints)) for (const k of Object.keys(m.vault_hints)) vaultScopes.add(k);
      const rank = isRiskTier(risk) ? RISK_TIER_RANK[risk] : -1;
      if (rank > highestRank) { highestRank = rank; highestRisk = riskTierLabel(risk); }
    }

    // ⛔ Op steps were invisible here: a recipe that calls only ops resolved
    // nothing, left nothing unresolved, and so earned "Read-only" — 297 shipped
    // recipes that write or send did (measured 2026-10-06). A kernel op resolves
    // through the registry; one that writes, or delivers a notification, is an
    // external change, as a write-tier ingredient of any outward kind is. A pack op
    // resolves through its pack's composition when the page holds that pack (its
    // own bundle's), grouped under the pack like an ingredient's operations;
    // otherwise it stays unresolved, so the page claims neither "Read-only" nor a
    // risk tier for the recipe.
    const writes = (risk: string): boolean => risk === 'write' || risk === 'admin' || risk === 'destructive';
    const raise = (risk: string): void => {
      const rank = isRiskTier(risk) ? RISK_TIER_RANK[risk] : -1;
      if (rank > highestRank) { highestRank = rank; highestRisk = riskTierLabel(risk); }
    };
    const packGrants = new Map<PackListing, { grant: RecipeGrant; rank: number }>();
    for (const call of opCallsOf(recipe)) {
      if (call.kernel !== undefined) {
        const { risk, label, delivers } = call.kernel;
        grants.push({
          name: call.op, kindLabel: label, risk: riskTierLabel(risk),
          external: delivers || writes(risk), reversible: false, operations: [],
        });
        connections.add(label);
        raise(risk);
        continue;
      }
      const resolved = resolvePackOp(call.op, packs);
      if (resolved === undefined) { unresolved++; continue; }
      let held = packGrants.get(resolved.pack);
      if (held === undefined) {
        held = {
          grant: { name: resolved.pack.name, kindLabel: 'Pack', risk: '', external: false, reversible: false, operations: [] },
          rank: -1,
        };
        packGrants.set(resolved.pack, held);
        grants.push(held.grant);
      }
      held.grant.operations.push({
        name: resolved.operation,
        risk: riskTierLabel(resolved.risk),
        approval: resolved.approval in APPROVAL_INTENT_LABEL ? APPROVAL_INTENT_LABEL[resolved.approval] : '',
        description: '',
      });
      if (writes(resolved.risk)) held.grant.external = true;
      const rank = isRiskTier(resolved.risk) ? RISK_TIER_RANK[resolved.risk] : -1;
      if (rank > held.rank) { held.rank = rank; held.grant.risk = riskTierLabel(resolved.risk); }
      raise(resolved.risk);
    }

    return {
      grants,
      highestRisk,
      externalWrites: grants.some((g) => g.external),
      anyReversible: grants.some((g) => g.reversible),
      connections: [...connections],
      vaultScopes: [...vaultScopes],
      warehouseRefs: extractWarehouseRefs(recipe),
      declaredPermissions,
      declaredExternalWrites: declaredPermissions.some((p) => isExternalWritePermission(p.code)),
      unresolved,
    };
  } catch {
    // ⛔ Fail closed: an empty summary renders "Read-only", a claim nothing checked.
    return { ...EMPTY_TRUST, unresolved: 1 };
  }
};

/** The line under a Trust & control list that does not hold everything the
 *  recipe or pack uses: without it, a partial list reads as complete. */
const notListedHere = (n: number): string =>
  n === 1 ? "1 more isn't listed here. You'll see it at install." : `${n} more aren't listed here. You'll see them at install.`;

/** Compact at-a-glance trust chips for the install panel: highest risk ·
 *  read-only-vs-external · reversible · provenance. */
const renderTrustChips = (summary: RecipeTrustSummary, recipe: RecipeListing['recipe']): string => {
  const chips: string[] = [];
  // A badge appears only when the page can back it up. While anything the recipe
  // uses is unresolved, the page shows no "Read-only" (it could write) and no
  // risk tier (the highest among what resolved could understate it) — and no
  // badge saying so: the owner, 2026-10-06, on "Permissions partly unverified":
  // to a normal user "unverified" reads as a safety warning, while it only meant
  // this page could not see everything. What holds anyway still shows: a known
  // external change, and the permissions the recipe declares (`requires`, known
  // from its body even with manifests retired).
  const complete = summary.unresolved === 0;
  if (complete && summary.highestRisk) {
    chips.push(`<span class="mp-tag" title="Highest risk tier across this recipe's ingredients">${e(summary.highestRisk)} access</span>`);
  }
  if (summary.externalWrites || summary.declaredExternalWrites) {
    chips.push('<span class="mp-tag mp-trust-warn" title="Installing lets this recipe change data in your connected accounts or services">Makes external changes</span>');
  } else if (summary.declaredPermissions.length > 0) {
    const n = summary.declaredPermissions.length;
    chips.push(`<span class="mp-tag" title="The recipe declares the permissions it needs — see Trust &amp; control">Declares ${n} permission${n === 1 ? '' : 's'}</span>`);
  } else if (complete) {
    chips.push('<span class="mp-tag" title="This recipe only reads — it makes no external changes">Read-only</span>');
  }
  if (summary.externalWrites && summary.anyReversible) {
    chips.push('<span class="mp-tag" title="At least one action declares a cancellation partner">Reversible</span>');
  }
  const provenance = renderProvenanceOptOutBadge({ recipe });
  if (provenance) chips.push(provenance);
  return chips.length > 0 ? `<div class="mp-card-tags mp-trust-chips">${chips.join('')}</div>` : '';
};

/** The full Trust & control section (main column): what it's allowed to do ·
 *  data it touches. Omits entirely when nothing resolves. */
const renderTrustControl = (summary: RecipeTrustSummary): string => {
  const hasGrants = summary.grants.length > 0;
  const hasData = summary.connections.length > 0 || summary.vaultScopes.length > 0 || summary.warehouseRefs.length > 0;
  const hasPermissions = summary.declaredPermissions.length > 0;
  if (!hasGrants && !hasData && !hasPermissions) return '';

  // Author-declared install permissions — the authoritative grant set Recued
  // enforces, sourced from the recipe body (`requires`). Survives the ingredient
  // manifest retirement that left per-ingredient operation grants unresolvable.
  const permissionsBlock = hasPermissions ? `
    <div class="mp-trust-group">
      <h4>Permissions it requires</h4>
      <div class="mp-card-tags">
        ${summary.declaredPermissions.map((p) => `<span class="mp-tag" title="${e(p.code)}">${e(p.label)}</span>`).join('')}
      </div>
      <span class="mp-trust-note">Declared by the recipe — Recued grants these at install, where you review them before approving.</span>
    </div>` : '';

  const grantsBlock = hasGrants ? `
    <div class="mp-trust-group">
      <h4>What it's allowed to do</h4>
      <div class="dep-list">
        ${summary.grants.map((g) => `
          <div class="dep-item mp-grant">
            ${g.risk ? `<span class="viz-type-badge">${e(g.risk)}</span>` : ''}
            <span class="mp-grant-kind">${e(g.kindLabel)}</span>
            <span class="mp-grant-name">${e(g.name)}</span>
            <span class="mp-grant-flags">
              ${g.external
                ? '<span class="mp-tag mp-trust-warn">external change</span>'
                : '<span class="mp-tag">read-only</span>'}
              ${g.reversible ? '<span class="mp-tag">reversible</span>' : ''}
            </span>
            ${g.operations.length > 0 ? `
              <div class="mp-grant-ops">
                ${g.operations.map((op) =>
                  `<span class="mp-tag"${op.description ? ` title="${e(op.description)}"` : ''}>${op.risk ? `${e(op.risk)}: ` : ''}${e(op.name)}${op.approval ? ` · ${e(op.approval)}` : ''}</span>`,
                ).join('')}
              </div>
            ` : ''}
          </div>
        `).join('')}
      </div>
      ${summary.unresolved > 0 ? `<p class="mp-trust-note">${notListedHere(summary.unresolved)}</p>` : ''}
    </div>` : '';

  const dataBlock = hasData ? `
    <div class="mp-trust-group">
      <h4>Data it touches</h4>
      ${summary.connections.length > 0 ? `
        <div class="mp-card-tags"><span class="mp-trust-label">Surfaces</span>${summary.connections.map((c) => `<span class="mp-tag">${e(c)}</span>`).join('')}</div>` : ''}
      ${summary.vaultScopes.length > 0 ? `
        <div class="mp-card-tags"><span class="mp-trust-label">Credentials</span>${summary.vaultScopes.map((v) => `<span class="mp-tag"><code>${e(v)}</code></span>`).join('')}</div>` : ''}
      ${summary.warehouseRefs.length > 0 ? `
        <div class="mp-card-tags"><span class="mp-trust-label">Warehouse</span>${summary.warehouseRefs.map((w) => `<span class="mp-tag"><code>${e(w)}</code></span>`).join('')}</div>` : ''}
    </div>` : '';

  return `
    <div class="mp-detail-section mp-trust-control">
      <h3>Trust &amp; control</h3>
      <p class="mp-trust-intro">What installing this recipe would let it do. Recued grants these permissions at install — review them there before approving.</p>
      ${permissionsBlock}
      ${grantsBlock}
      ${dataBlock}
    </div>
  `;
};

/** "Uses" — the ingredients a recipe references, as name + kind chips. NO link:
 *  ingredients are an internal unit with no public page (a `/ingredients` link
 *  404s per D-180). Revived from the formerly-dead `renderRecipeDependencies`. */
const renderRecipeUses = (
  recipe: RecipeListing['recipe'],
  allIngredients: IngredientListing[],
): string => {
  const slugs = extractIngredientSlugs(recipe);
  // ⛔ Op steps name no ingredient, so a recipe written in them listed nothing
  // here and the section vanished. An op is listed as written, with what it does;
  // a kernel op's badge says what it reaches, a pack op's that it is one.
  const ops = opCallsOf(recipe);
  if (slugs.length === 0 && ops.length === 0) return '';
  const bySlug = new Map(allIngredients.map((i) => [i.slug, i]));
  const counted = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`;
  const heading = [
    ...(slugs.length > 0 ? [counted(slugs.length, 'ingredient')] : []),
    ...(ops.length > 0 ? [counted(ops.length, 'operation')] : []),
  ].join(', ');
  const row = (kindLabel: string, name: string, code: string): string => `
            <div class="dep-item">
              <span class="viz-type-badge">${e(kindLabel)}</span>
              <span>${e(name)}</span>
              <code>${e(code)}</code>
            </div>
          `;
  return `
    <div class="mp-detail-section">
      <h3>Uses (${heading})</h3>
      <div class="dep-list">
        ${slugs.map((slug) => {
          const ing = bySlug.get(slug);
          const name = ing?.name ?? stripCorePrefix(slug);
          const kindLabel = ing ? marketplaceIngredientKindLabel(ingredientKindForListing(ing)) : 'Ingredient';
          return row(kindLabel, name, slug);
        }).join('')}
        ${ops.map((call) => row(call.kernel?.label ?? 'Pack operation', describeStep({ op: call.op }), call.op)).join('')}
      </div>
    </div>
  `;
};

/** D-b — "Fork lineage" when the recipe declares `metadata.fork_of`. Links to
 *  the upstream recipe's public detail page (`/recipes/<recipe_id>`). */
const renderForkLineage = (recipe: RecipeListing['recipe']): string => {
  const fork = recipe?.metadata?.fork_of;
  if (!fork || typeof fork.recipe_id !== 'string' || fork.recipe_id.length === 0) return '';
  const label = fork.author ? `${fork.author}/${fork.recipe_id}` : fork.recipe_id;
  const ver = typeof fork.version === 'number' ? ` v${fork.version}` : '';
  return `
    <div class="mp-detail-section mp-fork-lineage">
      <h3>Fork lineage</h3>
      <p>Forked from <a href="/recipes/${e(fork.recipe_id)}">${e(label)}</a>${e(ver)}.</p>
    </div>
  `;
};

/** "About" — one consolidated author/source block (R15): publisher identity →
 *  publisher page · source repo · website. Promotes what's scattered across the
 *  header + install panel into one clear place. */
const renderRecipeAbout = (state: MarketplaceState, r: RecipeListing): string => {
  const profile = findPublisherProfile(state, r.publisher_id);
  const repo = r.recipe?.metadata?.repo;
  const repoOk = typeof repo === 'string' && repo.startsWith('https://');
  const website = profile?.website;
  const websiteOk = typeof website === 'string' && website.startsWith('https://') && website !== repo;
  const links: string[] = [
    `<a href="${publisherHref(r.publisher_id)}" class="mp-publisher-link">${e(publisherDisplayName(state, r.publisher_id))}</a>${renderCertifiedPill(r.publisher_certified)}`,
  ];
  if (repoOk) links.push(`<a href="${e(repo)}" target="_blank" rel="noopener noreferrer">Source &amp; issues</a>`);
  if (websiteOk) links.push(`<a href="${e(website)}" target="_blank" rel="noopener noreferrer">Website</a>`);
  const bio = profile?.bio;
  return `
    <div class="mp-detail-section mp-about">
      <h3>About</h3>
      <div class="mp-about-row">
        <span class="mp-avatar" aria-hidden="true">${e(publisherInitials(state, r.publisher_id))}</span>
        <div class="mp-about-body">
          <div class="mp-about-links">${links.join('<span class="mp-dot">·</span>')}</div>
          ${bio ? `<p class="mp-about-bio">${e(bio)}</p>` : ''}
        </div>
      </div>
    </div>
  `;
};

interface PackRecipeDisplayRef {
  slug: string;
  version: number;
  visible: boolean;
}

/** Preserve the author's recipe display order while still using the shared
 *  v1/v2 projection as the shape/conflict gate. v2 `contents[]` is the display
 *  source when present; legacy `recipes[]` fills any refs not repeated there. */
const packRecipeDisplayRefs = (
  manifest: PackListing['manifest'],
): PackRecipeDisplayRef[] => {
  const projected = extractBulkPackRecipeRefs(manifest);
  if (projected === null || projected.length === 0) return [];
  const versionBySlug = new Map(projected.map((ref) => [ref.slug, ref.version]));
  const contents = Array.isArray(manifest?.contents) ? manifest.contents : [];
  const contentRecipes = contents.filter((entry) => entry?.type === 'recipe');
  const legacyRecipes = Array.isArray(manifest?.recipes) ? manifest.recipes : [];
  const authored = contentRecipes.length > 0
    ? [...contentRecipes, ...legacyRecipes]
    : legacyRecipes;
  const seen = new Set<string>();
  const refs: PackRecipeDisplayRef[] = [];

  for (const raw of authored) {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const entry = raw as Record<string, unknown>;
    const slug = entry.slug;
    const version = entry.version;
    if (
      typeof slug !== 'string'
      || typeof version !== 'number'
      || versionBySlug.get(slug) !== version
      || seen.has(slug)
    ) continue;
    seen.add(slug);
    refs.push({
      slug,
      version,
      visible: entry.type === 'recipe' ? entry.visible !== false : true,
    });
  }

  // Valid projected refs should normally all appear above. Keep the renderer
  // complete if an older stored shape was normalized differently.
  for (const ref of projected) {
    if (seen.has(ref.slug)) continue;
    refs.push({ ...ref, visible: true });
  }
  return refs;
};

/** A recipe-only pack may be v1 (`recipes[]`) or v2 (`contents[]`), but a v2
 *  pack with any non-recipe content keeps the generic typed-content renderer. */
const isRecipeOnlyPack = (manifest: PackListing['manifest']): boolean => {
  if (packRecipeDisplayRefs(manifest).length === 0) return false;
  const contents = manifest?.contents;
  return contents === undefined
    || (Array.isArray(contents) && contents.every((entry) => entry?.type === 'recipe'));
};

const recipeListingForPackRef = (
  recipes: ReadonlyArray<RecipeListing>,
  publisherId: string,
  ref: PackRecipeDisplayRef,
): RecipeListing | undefined => {
  const exact = recipes.filter((recipe) =>
    recipe.recipe_id === ref.slug && recipe.version === ref.version,
  );
  const samePublisher = exact.filter((recipe) => recipe.publisher_id === publisherId);
  if (samePublisher.length === 1) return samePublisher[0];
  if (samePublisher.length > 1) return undefined;
  return exact.length === 1 ? exact[0] : undefined;
};

const recipeNameFromPackSummary = (
  summary: PackTrustSummary | undefined,
  slug: string,
): string | undefined =>
  summary?.contents.find((item) => item.kind === 'recipe' && item.ref === slug)?.name;

const renderWorkflowRecipeCard = (args: {
  state: MarketplaceState;
  pack: PackListing;
  ref: PackRecipeDisplayRef;
  recipes: ReadonlyArray<RecipeListing>;
  summary: PackTrustSummary | undefined;
  currentRecipeId?: string;
}): string => {
  const { state, pack, ref, recipes, summary, currentRecipeId } = args;
  const listing = recipeListingForPackRef(recipes, pack.publisher_id, ref);
  const name = listing?.name
    || recipeNameFromPackSummary(summary, ref.slug)
    || ref.slug;
  const description = listing?.description || listing?.recipe?.metadata?.description;
  const recipeType = listing?.type || listing?.recipe?.metadata?.type;
  const tags = [...new Set([
    ...(listing?.platforms ?? listing?.recipe?.metadata?.supported_platforms ?? []),
    ...(listing?.tags ?? listing?.recipe?.metadata?.tags ?? []),
  ])].slice(0, 4);
  const current = currentRecipeId === ref.slug;
  const href = `/recipes/${encodeURIComponent(ref.slug)}`;

  return `
    <article class="mp-bundle-recipe${current ? ' mp-bundle-recipe-current' : ''}" data-recipe-ref="${e(ref.slug)}">
      <div class="mp-bundle-recipe-header">
        <a href="${href}" class="mp-bundle-recipe-name">${e(name)}</a>
        <div class="mp-card-tags">
          ${current ? '<span class="mp-tag mp-bundle-current-tag">This recipe</span>' : ''}
          ${ref.visible ? '' : '<span class="mp-tag">Background automation</span>'}
          ${recipeType ? `<span class="mp-tag">${e(recipeType)}</span>` : ''}
        </div>
      </div>
      ${description
        ? `<p class="mp-bundle-recipe-desc">${e(description)}</p>`
        : '<p class="mp-bundle-recipe-desc mp-bundle-recipe-unresolved">Recipe details are unavailable in this marketplace snapshot.</p>'}
      ${tags.length > 0 ? `<div class="mp-card-tags">${tags.map((tag) => `<span class="mp-tag">${e(tag)}</span>`).join('')}</div>` : ''}
      <div class="mp-bundle-recipe-meta">
        ${listing ? `<span>by ${renderPublisherLink(state, listing.publisher_id)}${renderCertifiedPill(listing.publisher_certified)}</span>` : ''}
        <span>pinned v${e(String(ref.version))}</span>
      </div>
    </article>
  `;
};

const BUNDLE_RECIPES_INLINE = 6;

const renderWorkflowRecipeGrid = (args: {
  state: MarketplaceState;
  pack: PackListing;
  refs: ReadonlyArray<PackRecipeDisplayRef>;
  recipes: ReadonlyArray<RecipeListing>;
  summary: PackTrustSummary | undefined;
  currentRecipeId?: string;
}): string => {
  const renderCard = (ref: PackRecipeDisplayRef): string =>
    renderWorkflowRecipeCard({ ...args, ref });
  const head = args.refs.slice(0, BUNDLE_RECIPES_INLINE);
  const rest = args.refs.slice(BUNDLE_RECIPES_INLINE);
  return `
    <div class="mp-bundle-recipe-grid">${head.map(renderCard).join('')}</div>
    ${rest.length > 0 ? `
      <details class="mp-pack-more mp-bundle-more">
        <summary>Show all ${args.refs.length} recipes</summary>
        <div class="mp-bundle-recipe-grid">${rest.map(renderCard).join('')}</div>
      </details>
    ` : ''}
  `;
};

/** The sticky install panel (conversion anchor, MP-5): primary CTA + at-a-glance
 *  trust chips + secondary actions + the copy-id pairing hint. */
const resolveRecipeBundleCarrier = (
  state: MarketplaceState,
  target: RecipeListing,
) => {
  const recipes = state.recipeBundleInstallContext?.recipes
    ?? state.recipes.map((recipe) => ({
      recipe_id: recipe.recipe_id,
      publisher_id: recipe.publisher_id,
      version: recipe.version,
      // Projected value first — a lean row has no body to read it from.
      ...(typeof (recipe.recipe_bundle ?? recipe.recipe?.metadata?.recipe_bundle) === 'string'
        ? { recipe_bundle: (recipe.recipe_bundle ?? recipe.recipe?.metadata?.recipe_bundle) as string }
        : {}),
    }));
  const packs = state.recipeBundleInstallContext?.packs
    ?? state.packs.flatMap((pack) => {
      const manifestIdentityMatches =
        pack.manifest?.slug === pack.slug
        && pack.manifest?.publisher === pack.publisher_id;
      return [{
        slug: pack.slug,
        publisher_id: pack.publisher_id,
        // The projection ran this same extraction behind the same identity
        // guard, so preferring it is not a weaker check — it is the same one,
        // made where the manifest still existed.
        recipe_refs: pack.recipe_refs
          ?? (manifestIdentityMatches ? extractBulkPackRecipeRefs(pack.manifest) ?? [] : []),
        listing: pack,
      }];
    });
  return resolveRecipeBundleInstallPack(
    {
      recipe_id: target.recipe_id,
      publisher_id: target.publisher_id,
      version: target.version,
      ...(typeof (target.recipe_bundle ?? target.recipe?.metadata?.recipe_bundle) === 'string'
        ? { recipe_bundle: (target.recipe_bundle ?? target.recipe?.metadata?.recipe_bundle) as string }
        : {}),
    },
    recipes,
    packs,
  );
};

/** Rich workflow context on an individually published bundled recipe. This is
 *  informational only; `resolveRecipeBundleCarrier` above remains the sole
 *  authority for whether the pack can own the install CTA. */
const renderRecipeBundleOverview = (
  state: MarketplaceState,
  target: RecipeListing,
): string => {
  const carrier = resolveRecipeBundleCarrier(state, target);
  if (carrier.status !== 'resolved') return '';
  const pack = carrier.pack.listing;
  const manifestRefs = packRecipeDisplayRefs(pack.manifest);
  const refs = manifestRefs.length > 0
    ? manifestRefs
    : carrier.pack.recipe_refs.map((ref) => ({ ...ref, visible: true }));
  if (refs.length === 0) return '';
  const current = refs.find((ref) => ref.slug === target.recipe_id);
  const orderedRefs = current
    ? [current, ...refs.filter((ref) => ref.slug !== target.recipe_id)]
    : refs;
  const listingByIdentity = new Map<string, RecipeListing>();
  for (const listing of [
    target,
    ...(state.recipeBundleInstallContext?.recipeListings ?? state.recipes),
  ]) {
    listingByIdentity.set(
      `${listing.publisher_id}/${listing.recipe_id}@${listing.version}`,
      listing,
    );
  }
  const recipes = [...listingByIdentity.values()];
  const packHref = `/packs/${encodeURIComponent(pack.slug)}`;
  return `
    <div class="mp-detail-section mp-bundle-overview">
      <h3>Complete workflow</h3>
      <div class="mp-bundle-heading">
        <a href="${packHref}" class="mp-bundle-pack-name">${e(pack.name)}</a>
        <span class="mp-tag">${refs.length} pinned recipe${refs.length === 1 ? '' : 's'}</span>
      </div>
      ${pack.description ? `<p class="mp-bundle-intro">${e(pack.description)}</p>` : ''}
      <p class="mp-bundle-note">You are viewing one recipe in this workflow. Installing it opens the complete pack so its other recipes and background automations arrive together.</p>
      ${renderWorkflowRecipeGrid({
        state,
        pack,
        refs: orderedRefs,
        recipes,
        summary: pack.trust_summary,
        currentRecipeId: target.recipe_id,
      })}
      <a href="${packHref}" class="mp-bundle-pack-link">View pack details, dependencies, and permissions &rarr;</a>
    </div>
  `;
};

const renderRecipeInstallPanel = (
  state: MarketplaceState,
  r: RecipeListing,
  summary: RecipeTrustSummary,
): string => {
  const hasDeclaredBundle =
    typeof (r.recipe_bundle ?? r.recipe?.metadata?.recipe_bundle) === 'string';
  const carrier = resolveRecipeBundleCarrier(state, r);
  const hasCarrier = carrier.status === 'resolved';
  const installUnavailable = hasDeclaredBundle && !hasCarrier;
  // Keep the recipe install intent even for a currently resolved bundle. The
  // webclient rechecks both catalogs at click time, then opens the named pack;
  // cached marketplace HTML can therefore never bypass a newer membership
  // drift by linking straight to an old carrier.
  const primaryHref = recipeInstallHref(r.recipe_id);
  const primaryLabel = hasCarrier
    ? 'Install complete workflow'
    : installUnavailable
      ? 'Workflow unavailable'
      : 'Install in Recued';
  const primaryTitle = hasCarrier
    ? `Review and install ${carrier.pack.recipe_refs.length} recipes as one pack`
    : installUnavailable
      ? 'The declared workflow pack is unavailable or out of date'
      : 'Install this recipe in Recued';
  // A bundled-recipe CTA installs the WHOLE carrier, so the one visible
  // recipe's trust summary is never a sound fallback for a missing pack
  // summary. In particular, a recipe with only canonical op steps can look
  // locally read-only while another member or core op writes. Fail closed
  // until the aggregate pack authority is available: no badge at all, as for
  // anything this page cannot back up (see `renderTrustChips`).
  const trustChips = hasCarrier
    ? carrier.pack.listing.trust_summary !== undefined
      ? renderPackTrustChips(carrier.pack.listing.trust_summary)
      : ''
    : renderTrustChips(summary, r.recipe);
  return `
    <aside class="mp-detail-aside">
      <div class="mp-install-panel">
        ${installUnavailable
          ? `<span class="btn-cta mp-install-cta" role="button" aria-disabled="true" title="${e(primaryTitle)}">${e(primaryLabel)}</span>`
          : `<a href="${e(primaryHref)}" class="btn-cta mp-install-cta" title="${e(primaryTitle)}"${hasCarrier ? ` data-recipe-bundle-pack="${e(carrier.pack.slug)}"` : ''}>${e(primaryLabel)}</a>`}
        ${hasCarrier ? `<p class="mp-install-hint">Installs the complete <a href="/packs/${encodeURIComponent(carrier.pack.slug)}">${e(carrier.pack.listing.name)}</a> pack with ${carrier.pack.recipe_refs.length} pinned recipe${carrier.pack.recipe_refs.length === 1 ? '' : 's'}. You review its contents and grants before install.</p>` : ''}
        ${installUnavailable ? '<p class="mp-install-hint">This recipe declares a bundled pack, so installing it alone is disabled. The publisher must restore the matching pack and version pins.</p>' : ''}
        ${trustChips}
        <div class="mp-install-actions">
          <a href="${publisherHref(r.publisher_id)}" class="btn-cta-secondary">View publisher</a>
          <a href="${KITCHEN_URL}" class="btn-cta-secondary">Open Kitchen</a>
          <button class="mp-copy-btn" data-action="copy" data-value="${e(hasCarrier ? carrier.pack.slug : r.recipe_id)}" title="${hasCarrier ? 'Copy pack slug' : 'Copy recipe ID'}">${hasCarrier ? 'Copy pack slug' : 'Copy ID'}</button>
        </div>
        <p class="mp-install-hint">
          If the webclient isn't paired yet, copy <code>${e(hasCarrier ? carrier.pack.slug : r.recipe_id)}</code> and paste it after pairing.
        </p>
      </div>
    </aside>
  `;
};

/** Detail view whose body has not arrived yet (or failed to). Renders the name
 *  it already knows plus an honest status — never a body-shaped page with
 *  everything zeroed out. */
const renderDetailBodyPending = (
  backHref: string,
  kind: 'recipe' | 'pack',
  name: string,
  error: string | null | undefined,
): string => `
    <a href="${backHref}" class="mp-back">&larr; Back to ${kind === 'recipe' ? 'recipes' : 'packs'}</a>
    <section class="mp-detail">
      <div class="mp-detail-header"><h2>${e(name)}</h2></div>
      ${error
        ? `<div class="mp-empty">
             <strong>Couldn't load this ${kind}</strong>
             <p>The listing is published, but its details didn't load. Check your connection and try again.</p>
             <p class="mp-error-detail">${e(error)}</p>
             <button type="button" class="btn-cta" data-action="retry-detail">Try again</button>
           </div>`
        : `<div class="mp-loading">Loading ${kind} details…</div>`}
    </section>
  `;

const renderRecipeDetail = (state: MarketplaceState): string => {
  const r = state.recipes.find((x) => x.recipe_id === state.detailSlug);
  if (!r) return `<div class="mp-empty">That recipe is not in the marketplace yet.</div>`;
  // The browse corpus carries no bodies. Without this branch every field below
  // resolves through `?.` to nothing and the page renders a COMPLETE-LOOKING
  // recipe with 0 steps, no permissions and an empty Trust & control section —
  // a listing that understates what it does, which is the one direction this
  // surface must never be wrong in.
  if (!r.recipe) return renderDetailBodyPending('/recipes', 'recipe', r.name, state.detailBodyError);
  const platforms = r.platforms ?? [];
  const tags = r.tags ?? [];
  const readme = r.recipe?.metadata?.readme;
  const repo = r.recipe?.metadata?.repo;
  const stepCount = (r.recipe?.prefetch_steps?.length ?? 0) + (r.recipe?.steps?.length ?? 0);
  const varCount = Object.keys(r.recipe?.variables ?? {}).length;
  const trust = summarizeRecipeTrust(
    r.recipe,
    state.ingredients,
    (state.recipeBundleInstallContext?.packs ?? []).map((pack) => pack.listing),
  );

  return `
    <a href="/recipes" class="mp-back">&larr; Back to recipes</a>
    <section class="mp-detail">
      <div class="mp-detail-header">
        <h2>${e(r.name)}</h2>
        <div class="mp-detail-meta">
          <span>by ${renderPublisherLink(state, r.publisher_id)}${renderCertifiedPill(r.publisher_certified)}</span>
          <span>v${e(String(r.version))}</span>
          ${r.type ? `<span>${e(r.type)}</span>` : ''}
          ${renderListingCounts(r.view_count, r.download_count)}
          ${renderStars(r.rating_avg ?? 0, r.rating_count ?? 0)}
          ${repo && repo.startsWith('https://') ? `<span><a href="${e(repo)}" target="_blank" rel="noopener noreferrer">Issues &amp; support</a></span>` : ''}
        </div>
      </div>

      <div class="mp-detail-layout">
        <div class="mp-detail-main">
          ${r.description ? `<p class="mp-detail-desc">${e(r.description)}</p>` : ''}

          ${renderRecipeBundleOverview(state, r)}

          ${readme ? `<div class="mp-readme">${renderMarkdown(readme)}</div>` : ''}

          ${renderRecipeVisualizer(r.recipe)}

          ${varCount > 0 ? renderVariablesSection(r.recipe?.variables ?? {}) : ''}

          ${renderRecipeUses(r.recipe, state.ingredients)}

          ${renderTrustControl(trust)}

          ${renderForkLineage(r.recipe)}

          ${renderRecipeAbout(state, r)}

          ${platforms.length > 0 || tags.length > 0 ? `
            <div class="mp-detail-section">
              <h3>Tags</h3>
              <div class="mp-card-tags">
                ${/* Platforms link to their hub — the internal path that makes the
                      hubs reachable by a reader and crawlable from 2,624 pages,
                      not just listed in a sitemap. Always safe: a platform this
                      recipe carries has at least one listing, so the URL is a
                      hub or a 301 to /recipes, never a 404. Tags stay inert —
                      there are 1,917 of them and no tag hubs. */''}
                ${platforms.map((p) =>
                  `<a class="mp-tag mp-tag-link" href="/recipes/platform/${encodeURIComponent(p.toLowerCase())}">${e(p)}</a>`,
                ).join('')}
                ${tags.map((t) => `<span class="mp-tag">${e(t)}</span>`).join('')}
              </div>
            </div>
          ` : ''}

          <div class="mp-detail-section">
            <h3>Details</h3>
            <div class="mp-detail-meta">
              <span><strong>${stepCount}</strong> steps</span>
              ${varCount > 0 ? `<span><strong>${varCount}</strong> configurable settings</span>` : ''}
              <span>recipe_id: <code>${e(r.recipe_id)}</code></span>
              ${renderProvenanceOptOutBadge(r)}
            </div>
          </div>

          ${renderRelatedRecipes(r, state.related ?? state.recipes)}
        </div>

        ${renderRecipeInstallPanel(state, r, trust)}
      </div>
    </section>
  `;
};

// ── Dependency graph ──

/** Extract ingredient slugs from a recipe's prefetch + sequential steps.
 *  Exported so the SSR worker can mount the same targeted manifest fetch
 *  (D-a) it uses to populate `state.ingredients` at detail render. */
export const extractIngredientSlugs = (recipe: RecipeBody | undefined): string[] => {
  const slugs = new Set<string>();
  for (const step of (recipe?.prefetch_steps ?? []) as Array<Record<string, unknown>>) {
    if (typeof step.ingredient === 'string') slugs.add(step.ingredient);
  }
  for (const step of (recipe?.steps ?? []) as Array<Record<string, unknown>>) {
    if (typeof step.ingredient === 'string') slugs.add(step.ingredient);
  }
  return [...slugs];
};

/** Render "Works well with" — recipes sharing platform + tags, ranked by overlap. */
const renderRelatedRecipes = (
  current: RecipeListing,
  allRecipes: RecipeListing[],
): string => {
  const platforms = new Set(current.platforms ?? []);
  const tags = new Set(current.tags ?? []);
  if (platforms.size === 0 && tags.size === 0) return '';

  const scored = allRecipes
    .filter((r) => r.recipe_id !== current.recipe_id)
    .map((r) => {
      const platformMatch = (r.platforms ?? []).some((p) => platforms.has(p)) ? 2 : 0;
      const tagOverlap = (r.tags ?? []).filter((t) => tags.has(t)).length;
      return { r, score: platformMatch + tagOverlap };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4);

  if (scored.length === 0) return '';

  return `
    <div class="mp-detail-section">
      <h3>Works well with</h3>
      <div class="mp-grid" style="gap: 10px;">
        ${scored.map(({ r }) => `
          <a href="/recipes/${e(r.recipe_id)}" class="mp-card" style="padding: 12px 14px; gap: 4px;">
            <span class="mp-card-name" style="font-size: 13px;">${e(r.name)}</span>
            <span class="mp-card-desc" style="font-size: 11px;">${e(r.description.length > 80 ? r.description.slice(0, 77) + '...' : r.description)}</span>
          </a>
        `).join('')}
      </div>
    </div>
  `;
};

// ── Pack detail ──

/** Type guard for a precomputed content row (`PackTrustSummary.contents`). */
type PackContentRow = PackTrustSummary['contents'][number];

/** Kind tag copy, closed over the union. The fallback is deliberate: a row
 *  written by a NEWER precompute than this renderer knows must still say
 *  something — the old two-way ternary labelled every third kind "Ingredient". */
const PACK_CONTENT_KIND_LABEL: Record<PackTrustContentKind, string> = {
  recipe: 'Recipe',
  ingredient: 'Ingredient',
  reception_template: 'Form template',
};

const renderPackContentRow = (item: PackContentRow): string => {
  // Recipes link to their public detail page; ingredients are an internal
  // unit with no public page (a `/ingredients` link 404s per D-180) → plain;
  // a pack-shipped form template lives inside the pack and is reviewed in
  // Reception after install → plain too.
  const href = item.kind === 'recipe' ? `/recipes/${encodeURIComponent(item.ref)}` : null;
  const label = e(item.name || item.ref);
  const main = href ? `<a href="${href}">${label}</a>` : `<span>${label}</span>`;
  const kindTag = `<span class="mp-tag">${PACK_CONTENT_KIND_LABEL[item.kind] ?? 'Item'}</span>`;
  return `<li>${main} ${kindTag}</li>`;
};

/** Degrade path for rows published before the precompute — the raw manifest
 *  `contents[]` as display rows (slug as name, no resolution). Recipe-only
 *  packs are intercepted by the richer renderer below, including v1 packs
 *  that use only top-level `recipes[]`. */
const fallbackPackContents = (manifest: PackListing['manifest']): PackContentRow[] => {
  const rows: PackContentRow[] = [];
  for (const c of manifest?.contents ?? []) {
    // `contents` is publisher-controlled: a null element must not throw.
    if (c === null || typeof c !== 'object' || c.visible === false) continue;
    if ((c.type === 'recipe' || c.type === 'ingredient') && c.slug) {
      rows.push({ kind: c.type === 'recipe' ? 'recipe' : 'ingredient', ref: c.slug, name: c.slug });
    } else if (c.type === 'reception_template' && typeof c.template?.template_ref === 'string' && c.template.template_ref) {
      const ref = c.template.template_ref;
      rows.push({ kind: 'reception_template', ref, name: c.template.name || ref });
    }
  }
  return rows;
};

/** Largest "What this pack installs" list shown inline before the rest folds
 *  into a script-free `<details>` "Show all N" (R16 — collapsible, no tabs). */
const PACK_CONTENTS_INLINE = 8;

const renderPackContents = (
  summary: PackTrustSummary | undefined,
  manifest: PackListing['manifest'],
): string => {
  const items = summary?.contents?.length ? summary.contents : fallbackPackContents(manifest);
  if (items.length === 0) return '';
  const head = items.slice(0, PACK_CONTENTS_INLINE).map(renderPackContentRow).join('\n');
  const rest = items.slice(PACK_CONTENTS_INLINE);
  const more = rest.length > 0 ? `
      <details class="mp-pack-more">
        <summary>Show all ${items.length}</summary>
        <ul class="mp-pack-contents">${rest.map(renderPackContentRow).join('\n')}</ul>
      </details>` : '';
  return `
    <div class="mp-detail-section">
      <h3>What this pack installs</h3>
      <ul class="mp-pack-contents">${head}</ul>${more}
    </div>
  `;
};

/** Recipe-only BulkPackManifest detail. Unlike the generic typed-content list,
 *  this resolves the marketplace listings into readable workflow cards and
 *  explicitly identifies supporting recipes hidden from the installed UI. */
const renderRecipeOnlyPackContents = (
  state: MarketplaceState,
  pack: PackListing,
  summary: PackTrustSummary | undefined,
): string => {
  if (!isRecipeOnlyPack(pack.manifest)) return '';
  const refs = packRecipeDisplayRefs(pack.manifest);
  const backgroundCount = refs.filter((ref) => !ref.visible).length;
  return `
    <div class="mp-detail-section mp-bundle-overview mp-pack-recipe-overview">
      <h3>What this pack installs</h3>
      <div class="mp-bundle-heading">
        <span class="mp-bundle-pack-name">Included recipes</span>
        <span class="mp-tag">${refs.length} pinned recipe${refs.length === 1 ? '' : 's'}</span>
      </div>
      <p class="mp-bundle-intro">This pack installs the complete recipe workflow together.${backgroundCount > 0 ? ` ${backgroundCount} supporting recipe${backgroundCount === 1 ? '' : 's'} run${backgroundCount === 1 ? 's' : ''} as background automation.` : ''}</p>
      ${renderWorkflowRecipeGrid({
        state,
        pack,
        refs,
        recipes: state.recipes,
        summary,
      })}
    </div>
  `;
};

/** Compact at-a-glance trust chips for the pack install panel: highest risk ·
 *  read-only-vs-external · reversible · item count. */
const renderPackTrustChips = (summary: PackTrustSummary): string => {
  const chips: string[] = [];
  // Same rule as a recipe's (`renderTrustChips`): while items are unresolved, no
  // "Read-only" (they could write), no risk tier (it could understate), and no
  // badge saying so.
  const complete = summary.unresolved === 0;
  if (complete && summary.highestRisk) {
    chips.push(`<span class="mp-tag" title="Highest risk tier across everything this pack installs">${e(summary.highestRisk)} access</span>`);
  }
  if (summary.externalWrites) {
    chips.push('<span class="mp-tag mp-trust-warn" title="Installing lets this pack change data in your connected accounts or services">Makes external changes</span>');
  } else if (complete) {
    chips.push('<span class="mp-tag" title="This pack only reads — it makes no external changes">Read-only</span>');
  }
  if (summary.externalWrites && summary.anyReversible) {
    chips.push('<span class="mp-tag" title="At least one action declares a cancellation partner">Reversible</span>');
  }
  if (summary.contents.length > 0) {
    chips.push(`<span class="mp-tag" title="What this pack installs">${summary.contents.length} item${summary.contents.length === 1 ? '' : 's'}</span>`);
  }
  return chips.length > 0 ? `<div class="mp-card-tags mp-trust-chips">${chips.join('')}</div>` : '';
};

/** The full aggregate Trust & control section (main column) — union of what
 *  the whole bundle is allowed to do. Reads the render-ready precompute, so
 *  this is a thin 1:1 pass (no manifest introspection at render). Reuses the
 *  6c recipe-trust CSS. Omits entirely when nothing resolved. */
const renderPackTrustControl = (summary: PackTrustSummary): string => {
  const hasGrants = summary.grants.length > 0;
  const hasData = summary.connections.length > 0 || summary.vaultScopes.length > 0 || summary.warehouseRefs.length > 0;
  if (!hasGrants && !hasData) return '';

  const grantsBlock = hasGrants ? `
    <div class="mp-trust-group">
      <h4>What it's allowed to do</h4>
      <div class="dep-list">
        ${summary.grants.map((g) => `
          <div class="dep-item mp-grant">
            ${g.risk ? `<span class="viz-type-badge">${e(g.risk)}</span>` : ''}
            <span class="mp-grant-kind">${e(g.kindLabel)}</span>
            <span class="mp-grant-name">${e(g.name)}</span>
            <span class="mp-grant-flags">
              ${g.external
                ? '<span class="mp-tag mp-trust-warn">external change</span>'
                : '<span class="mp-tag">read-only</span>'}
              ${g.reversible ? '<span class="mp-tag">reversible</span>' : ''}
            </span>
            ${g.operations.length > 0 ? `
              <div class="mp-grant-ops">
                ${g.operations.map((op) =>
                  `<span class="mp-tag"${op.title ? ` title="${e(op.title)}"` : ''}>${e(op.text)}</span>`,
                ).join('')}
              </div>
            ` : ''}
          </div>
        `).join('')}
      </div>
      ${summary.unresolved > 0 ? `<p class="mp-trust-note">${notListedHere(summary.unresolved)}</p>` : ''}
    </div>` : '';

  const dataBlock = hasData ? `
    <div class="mp-trust-group">
      <h4>Data it touches</h4>
      ${summary.connections.length > 0 ? `
        <div class="mp-card-tags"><span class="mp-trust-label">Surfaces</span>${summary.connections.map((c) => `<span class="mp-tag">${e(c)}</span>`).join('')}</div>` : ''}
      ${summary.vaultScopes.length > 0 ? `
        <div class="mp-card-tags"><span class="mp-trust-label">Credentials</span>${summary.vaultScopes.map((v) => `<span class="mp-tag"><code>${e(v)}</code></span>`).join('')}</div>` : ''}
      ${summary.warehouseRefs.length > 0 ? `
        <div class="mp-card-tags"><span class="mp-trust-label">Warehouse</span>${summary.warehouseRefs.map((w) => `<span class="mp-tag"><code>${e(w)}</code></span>`).join('')}</div>` : ''}
    </div>` : '';

  return `
    <div class="mp-detail-section mp-trust-control">
      <h3>Trust &amp; control</h3>
      <p class="mp-trust-intro">What installing this whole pack would let it do. Recued grants these permissions at install — review them there before approving.</p>
      ${grantsBlock}
      ${dataBlock}
    </div>
  `;
};

/** "About" — one consolidated author/source block (R15), mirroring the recipe
 *  About. The pack's source repo lives on `manifest.repo` (the dashboard
 *  About edit / publish-pack `repo` override). */
const renderPackAbout = (state: MarketplaceState, p: PackListing): string => {
  const profile = findPublisherProfile(state, p.publisher_id);
  const repo = (p.manifest as Record<string, unknown>)?.repo;
  const repoOk = typeof repo === 'string' && repo.startsWith('https://');
  const website = profile?.website;
  const websiteOk = typeof website === 'string' && website.startsWith('https://') && website !== repo;
  const links: string[] = [
    `<a href="${publisherHref(p.publisher_id)}" class="mp-publisher-link">${e(publisherDisplayName(state, p.publisher_id))}</a>${renderCertifiedPill(p.publisher_certified)}`,
  ];
  if (repoOk) links.push(`<a href="${e(repo)}" target="_blank" rel="noopener noreferrer">Source &amp; issues</a>`);
  if (websiteOk) links.push(`<a href="${e(website)}" target="_blank" rel="noopener noreferrer">Website</a>`);
  const bio = profile?.bio;
  return `
    <div class="mp-detail-section mp-about">
      <h3>About</h3>
      <div class="mp-about-row">
        <span class="mp-avatar" aria-hidden="true">${e(publisherInitials(state, p.publisher_id))}</span>
        <div class="mp-about-body">
          <div class="mp-about-links">${links.join('<span class="mp-dot">·</span>')}</div>
          ${bio ? `<p class="mp-about-bio">${e(bio)}</p>` : ''}
        </div>
      </div>
    </div>
  `;
};

/** The sticky pack install panel (conversion anchor, MP-5 parity): primary CTA
 *  + at-a-glance aggregate trust chips + secondary actions + pairing hint. */
const renderPackInstallPanel = (
  state: MarketplaceState,
  p: PackListing,
  summary: PackTrustSummary | undefined,
): string => `
  <aside class="mp-detail-aside">
    <div class="mp-install-panel">
      <a href="${e(packInstallHref(p.slug))}" class="btn-cta mp-install-cta" title="Install this pack in Recued">
        Install in Recued
      </a>
      ${summary ? renderPackTrustChips(summary) : ''}
      <div class="mp-install-actions">
        <a href="${publisherHref(p.publisher_id)}" class="btn-cta-secondary">View publisher</a>
        <button class="mp-copy-btn" data-action="copy" data-value="${e(p.slug)}" title="Copy pack slug">Copy slug</button>
      </div>
      <p class="mp-install-hint">
        If the webclient isn't paired yet, copy <code>${e(p.slug)}</code> and paste it after pairing.
      </p>
    </div>
  </aside>
`;

const renderPackDependencies = (
  dependencies: NonNullable<PackListing['manifest']>['dependencies'],
): string => {
  const deps = (dependencies ?? []).filter((d) => d.slug);
  if (deps.length === 0) return '';
  const rows = deps.map((d) => {
    const href = d.slug ? `/packs/${encodeURIComponent(d.slug)}` : null;
    const ver = d.min_version ? ` <span class="mp-tag">v${e(String(d.min_version))}+</span>` : '';
    const label = e(d.slug ?? '');
    return `<li>${href ? `<a href="${href}">${label}</a>` : `<code>${label}</code>`}${ver}</li>`;
  }).join('\n');
  return `
    <div class="mp-detail-section">
      <h3>Builds on</h3>
      <ul class="mp-pack-deps">${rows}</ul>
    </div>
  `;
};

const renderPackDetail = (state: MarketplaceState): string => {
  const p = state.packs.find((x) => x.slug === state.detailSlug);
  if (!p) return `<div class="mp-empty">That pack is not in the marketplace yet.</div>`;
  // Same reason as `renderRecipeDetail`: without the manifest this page would
  // claim the pack installs nothing.
  if (!p.manifest) return renderDetailBodyPending('/packs', 'pack', p.name, state.detailBodyError);
  const tags = p.tags ?? [];
  const kind = p.pack_kind || p.manifest?.pack_kind;
  // Precomputed at publish/seed (§12B). Absent on pre-migration rows → the
  // Trust & control section + at-a-glance chips degrade out; the rest renders.
  const summary = p.trust_summary;
  const recipeOnlyContents = renderRecipeOnlyPackContents(state, p, summary);

  return `
    <a href="/packs" class="mp-back">&larr; Back to packs</a>
    <section class="mp-detail">
      <div class="mp-detail-header">
        <h2>${e(p.name)}</h2>
        <div class="mp-detail-meta">
          <span>by ${renderPublisherLink(state, p.publisher_id)}${renderCertifiedPill(p.publisher_certified)}</span>
          <span>v${e(String(p.version))}</span>
          ${kind ? `<span>${e(kind)}</span>` : ''}
          ${renderListingCounts(p.view_count, p.download_count)}
        </div>
      </div>

      <div class="mp-detail-layout">
        <div class="mp-detail-main">
          ${p.description ? `<p class="mp-detail-desc">${e(p.description)}</p>` : ''}

          ${recipeOnlyContents || renderPackContents(summary, p.manifest)}

          ${renderPackDependencies(p.manifest?.dependencies)}

          ${summary ? renderPackTrustControl(summary) : ''}

          ${renderPackAbout(state, p)}

          ${tags.length > 0 ? `
            <div class="mp-detail-section">
              <h3>Tags</h3>
              <div class="mp-card-tags">
                ${tags.map((t) => `<span class="mp-tag">${e(t)}</span>`).join('')}
              </div>
            </div>
          ` : ''}
        </div>

        ${renderPackInstallPanel(state, p, summary)}
      </div>
    </section>
  `;
};

// ── Publisher page ──

const renderPublisherPage = (state: MarketplaceState): string => {
  const handle = state.detailSlug ?? '';
  const recipes = publisherRecipes(state, handle);
  const packs = state.packs.filter((p) => p.publisher_id === handle);
  const profile = findPublisherProfile(state, handle);
  // Rows, not stats: the fetch clamps the page against the publisher's real
  // recipe count, so a window can only be empty when the publisher genuinely
  // has nothing — a stats-aware branch here could never differ, and an
  // unreachable guard is worse than none (it reads as protection).
  if (!handle || (!profile && recipes.length === 0 && packs.length === 0)) {
    return `
      <a href="/" class="mp-back">&larr; Back to store</a>
      <div class="mp-empty">
        <strong>Publisher not found</strong>
        <p>That namespace does not have public marketplace listings.</p>
      </div>
    `;
  }

  const display = publisherDisplayName(state, handle);
  // `stats` wins wherever present: on a server-paged render `recipes` is 48
  // rows, and counting them would publish "48 recipes · 0 installs" on a
  // profile that has thousands.
  const stats = state.publisherStats;
  const recipeTotal = stats?.recipes ?? recipes.length;
  const packTotal = stats?.packs ?? packs.length;
  const installs = stats?.installs ?? publisherInstallTotal(recipes);
  const ratingCount = stats?.ratings ?? recipes.reduce((sum, r) => sum + (r.rating_count ?? 0), 0);
  const latest = stats?.latest ?? latestPublishAt(recipes, packs);
  const website = profile?.website?.trim() ?? '';
  const certified = profile?.certified
    || recipes.some((r) => r.publisher_certified)
    || packs.some((p) => p.publisher_certified);
  const profileStub = !profile || !profile.display_name || !profile.avatar_url;

  // §12D — sections by default; a long listing switches to tabs (Recipes |
  // Packs) so you don't scroll past 50 recipes to reach packs (R16). No trust
  // info to bury here, so tabs are fine (unlike pack detail).
  // Server-paged ⇒ the rows ARE the window. Otherwise slice here, so the SPA
  // and every unit test keep the same bounded page.
  const shownRecipes = stats ? recipes : pageSlice(recipes, state.page);
  const shownPacks = stats ? packs : packs.slice(0, BROWSE_PAGE_SIZE);
  const recipePage = clampPage(state.page, recipeTotal);
  const recipesGrid = shownRecipes.length > 0
    ? `<div class="mp-grid">${shownRecipes.map((r) => renderRecipeCard(r, state)).join('')}</div>`
      + renderPagination(publisherHref(handle), recipePage, recipeTotal)
    : '<div class="mp-empty">This publisher has no public recipes yet.</div>';
  // Packs are a PREVIEW here, not a page — one control cannot honestly drive
  // two lists of different lengths, so past the preview the reader follows a
  // link into the pack browse filtered to this publisher.
  const packsGrid = shownPacks.length > 0
    ? `<div class="mp-grid">${shownPacks.map((p) => renderPackCard(p, state)).join('')}</div>`
      + (packTotal > shownPacks.length
        ? `<p class="mp-result-count"><a href="/packs?search=${encodeURIComponent(handle)}">View all ${packTotal} packs by ${e(display)} &rarr;</a></p>`
        : '')
    : '<div class="mp-empty">This publisher has no public packs yet.</div>';
  const useTabs = recipeTotal > PUBLISHER_TABS_THRESHOLD || packTotal > PUBLISHER_TABS_THRESHOLD;
  const listings = useTabs
    ? renderPublisherTabs(recipeTotal, packTotal, recipesGrid, packsGrid)
    : `
      <section class="publisher-section">
        <div class="landing-section-head">
          <h2>Recipes</h2>
          <a href="/recipes">Browse all recipes</a>
        </div>
        ${recipesGrid}
      </section>

      <section class="publisher-section">
        <div class="landing-section-head">
          <h2>Packs</h2>
          <a href="/packs">Browse all packs</a>
        </div>
        ${packsGrid}
      </section>`;

  return `
    <a href="/" class="mp-back">&larr; Back to store</a>
    <section class="publisher-page">
      <div class="publisher-header">
        ${renderPublisherAvatar(state, handle)}
        <div class="publisher-header-main">
          <div class="publisher-kicker">@${e(handle)}${certified ? renderCertifiedPill(true) : ''}</div>
          <h1>${e(display)}</h1>
          ${profile?.bio ? `<p>${e(profile.bio)}</p>` : '<p>Public profile details are pending; marketplace listings for this namespace are shown below.</p>'}
          <div class="publisher-actions">
            <button class="mp-copy-btn" data-action="copy" data-value="${e(publisherPublicUrl(handle))}">Copy publisher URL</button>
            ${website.startsWith('https://') ? `<a href="${e(website)}" class="mp-copy-btn" target="_blank" rel="noopener noreferrer">Website</a>` : ''}
            <a href="${DASHBOARD_PUBLISH_URL}" class="mp-install-btn">Publish under your handle</a>
          </div>
          ${profileStub ? '<div class="publisher-profile-note">Profile uses public publisher fields; avatar image falls back to initials until that field ships.</div>' : ''}
        </div>
      </div>

      <div class="publisher-stats">
        <span class="mp-stat"><strong>${recipeTotal}</strong> recipes</span>
        <span class="mp-stat"><strong>${packTotal}</strong> packs</span>
        ${installs > 0 ? `<span class="mp-stat"><strong>${installs.toLocaleString()}</strong> installs</span>` : ''}
        <span class="mp-stat"><strong>${ratingCount}</strong> ratings</span>
        <span class="mp-stat"><strong>${e(renderDate(latest))}</strong> latest</span>
      </div>

      <div class="mp-detail-section">
        <h3>Public URL</h3>
        <div class="mp-json-url">${e(publisherPublicUrl(handle))}</div>
      </div>

      ${listings}
    </section>
  `;
};

/** §12D — switch the publisher page from stacked sections to tabs once EITHER
 *  listing exceeds this. Below it, both lists fit on one scroll. */
const PUBLISHER_TABS_THRESHOLD = 12;

/** Script-free Recipes | Packs tabs (pure-CSS radio-tabs, SSR-safe, no JS — the
 *  SPA renders the same markup). Defaults to the Recipes tab unless it's empty
 *  and Packs isn't. The radios / labels / panels are siblings so the CSS
 *  `:checked ~` combinators can toggle the active panel + label. */
const renderPublisherTabs = (
  recipeCount: number,
  packCount: number,
  recipesGrid: string,
  packsGrid: string,
): string => {
  const recipesActive = recipeCount > 0 || packCount === 0;
  return `
      <div class="publisher-tabs">
        <input type="radio" name="pub-tab" id="pub-tab-recipes" class="publisher-tab-radio"${recipesActive ? ' checked' : ''}>
        <input type="radio" name="pub-tab" id="pub-tab-packs" class="publisher-tab-radio"${recipesActive ? '' : ' checked'}>
        <div class="publisher-tab-labels" role="tablist">
          <label for="pub-tab-recipes">Recipes <span class="publisher-tab-count">${recipeCount}</span></label>
          <label for="pub-tab-packs">Packs <span class="publisher-tab-count">${packCount}</span></label>
        </div>
        <section class="publisher-tab-panel publisher-panel-recipes">
          <div class="landing-section-head"><a href="/recipes">Browse all recipes</a></div>
          ${recipesGrid}
        </section>
        <section class="publisher-tab-panel publisher-panel-packs">
          <div class="landing-section-head"><a href="/packs">Browse all packs</a></div>
          ${packsGrid}
        </section>
      </div>`;
};

// ── Listing views ──

/** ⛔ THIS FILE HOLDS NO SEARCH CODE.
 *
 *  `filterRecipes` / `sortRecipes` / `filterPacks` / `sortPacks` and their
 *  ranking helpers (`CERTIFIED_RANKING_BOOST`, `popularScore`, `updatedKey`)
 *  lived here and were deleted. What matches, how it ranks and how facets are
 *  counted is defined ONCE — `apps/webclient/src/discover/discover-specs.ts`,
 *  reproduced in SQL by `discover_listings` (migrations 029/030) and proven
 *  equal by `scripts/verify-discover-parity.mjs`. Both catalogue consumers read
 *  that one definition: the webclient runs it, the SPA calls it over
 *  `/catalog/search`.
 *
 *  A second copy here would fork it silently — the marketplace and the webclient
 *  would return different answers to the same query and neither would fail. So
 *  the renderers below take the rows they are given, in the order they are given
 *  them, and never re-filter or re-sort. */

const renderResultCount = (count: number, total: number, label: string): string => {
  if (count === total) return `<p class="mp-result-count">${total} ${label}</p>`;
  return `<p class="mp-result-count">${count} of ${total} ${label}</p>`;
};

/** Browse page size. 48 keeps a list page around 85 KB; the whole catalogue on
 *  one page was 4.6 MB of HTML at 2,624 recipes, which is hostile on mobile and
 *  burns crawl budget on a page whose SEO job is internal linking, not discovery
 *  (the sitemap carries every detail URL — that is what gets indexed). */
export const BROWSE_PAGE_SIZE = 48;

export const pageCount = (total: number): number =>
  Math.max(1, Math.ceil(total / BROWSE_PAGE_SIZE));

/** Clamp a requested page into range. An out-of-range `?page=` is a stale link
 *  or a crawler guess: clamp rather than 404 so the URL still returns content. */
export const clampPage = (page: number, total: number): number =>
  Math.min(Math.max(1, Math.floor(page) || 1), pageCount(total));

export const pageSlice = <T>(rows: readonly T[], page: number): T[] => {
  const p = clampPage(page, rows.length);
  return rows.slice((p - 1) * BROWSE_PAGE_SIZE, p * BROWSE_PAGE_SIZE);
};

/** Reconcile the two paging modes for one list render.
 *
 *  `corpusSize` present ⇒ the database already returned exactly this page
 *  (`held` is one window out of `corpusSize`). Absent ⇒ `held` IS the corpus
 *  and the renderer slices it itself.
 *
 *  The two denominators are NOT the same number and collapsing them breaks one
 *  mode or the other:
 *   • `pageTotal` drives the pagination control, and in memory it walks the
 *     FILTERED set — a search matching twice must not leave the reader on an
 *     empty page 40 of the unfiltered catalogue.
 *   • `totalCount` is the "N of M" denominator — the whole set in hand.
 *
 *  Server paging is not filtering: nothing was removed, so page 1 of a 2,624-row
 *  catalogue reads "2,624 recipes" beside "Page 1 of 55", not "48 of 2,624". */
const listCounts = <T>(
  filtered: readonly T[],
  held: readonly T[],
  corpusSize: number | undefined,
  /** Server-SEARCHED: how many rows the active query matched. Present ⇒ `held`
   *  is one window of `matchCount` matches drawn from a `corpusSize` catalogue,
   *  and BOTH numbers are load-bearing — the pager must walk the matches, the
   *  count line must keep the catalogue as its denominator ("42 of 1,932"). */
  matchCount?: number,
): { alreadyPaged: boolean; pageTotal: number; shownCount: number; totalCount: number } => {
  if (matchCount !== undefined && corpusSize !== undefined) {
    return {
      alreadyPaged: true,
      pageTotal: matchCount,
      shownCount: matchCount,
      totalCount: corpusSize,
    };
  }
  const alreadyPaged = corpusSize !== undefined;
  // A client filter re-narrows whatever is in hand — including a server-served
  // page — so the counts fall back to describing the rows present.
  if (corpusSize === undefined || filtered.length !== held.length) {
    return { alreadyPaged, pageTotal: filtered.length, shownCount: filtered.length, totalCount: held.length };
  }
  return { alreadyPaged, pageTotal: corpusSize, shownCount: corpusSize, totalCount: corpusSize };
};

/** Prev/next + position. Plain `<a href>` so crawlers can walk the set —
 *  `rel=next/prev` is not used (Google dropped it in 2019) and the pages are NOT
 *  noindexed or canonicalled back to page 1, which is the classic mistake that
 *  de-indexes the tail. Each page self-canonicals via the document renderer. */
const renderPagination = (basePath: string, page: number, total: number): string => {
  const pages = pageCount(total);
  if (pages <= 1) return '';
  const href = (n: number): string => (n === 1 ? basePath : `${basePath}?page=${n}`);
  const prev = page > 1
    ? `<a class="mp-page-prev" href="${e(href(page - 1))}" rel="prev">Previous</a>`
    : '<span class="mp-page-prev is-disabled" aria-disabled="true">Previous</span>';
  const next = page < pages
    ? `<a class="mp-page-next" href="${e(href(page + 1))}" rel="next">Next</a>`
    : '<span class="mp-page-next is-disabled" aria-disabled="true">Next</span>';
  return `<nav class="mp-pagination" aria-label="Pagination">${prev}`
    + `<span class="mp-page-pos">Page ${page} of ${pages}</span>${next}</nav>`;
};

const renderRecipeList = (state: MarketplaceState): string => {
  // The rows ARE the answer. Both producers — the SSR read and the search
  // endpoint — hand over exactly the rows this page shows, already narrowed,
  // ranked and paged. On a facet hub that set IS the facet, and `totals` there
  // is the facet's own count (not the catalogue's), which is what keeps the hub
  // reading "102 recipes" rather than "102 of 2,624".
  const filtered = state.recipes;
  const { alreadyPaged, pageTotal, shownCount, totalCount } =
    listCounts(filtered, state.recipes, state.totals?.recipes, state.matched?.recipes);
  const countLine = renderResultCount(shownCount, totalCount, 'recipes');
  if (filtered.length === 0) {
    const hasFilters = state.search || state.platformFilter || state.tagFilter;
    return `${countLine}<div class="mp-empty">${hasFilters ? 'Nothing matches those filters yet — try clearing one to widen the set.' : 'No recipes published yet.'}</div>`;
  }
  // Paginate AFTER filtering: the page walks the filtered set, so a narrowed
  // search does not leave the reader on an empty page 40.
  const shown = alreadyPaged ? filtered : pageSlice(filtered, state.page);
  const page = clampPage(state.page, pageTotal);
  // A facet hub paginates WITHIN its facet.
  const basePath = state.facet
    ? `/recipes/${state.facet.kind}/${encodeURIComponent(state.facet.value)}`
    : '/recipes';
  return `${countLine}<div class="mp-grid">${shown.map((r) => renderRecipeCard(r, state)).join('')}</div>`
    + renderPagination(basePath, page, pageTotal);
};

/** A pack card for the `/packs` browse grid + landing packs section. Packs are
 *  a first-class marketplace unit (bundles of recipes); ingredients are
 *  internal and not surfaced. */
const renderPackCard = (
  p: PackListing,
  state?: Pick<MarketplaceState, 'publishers'>,
): string => {
  const tags = p.tags ?? [];
  const publisher = state
    ? renderPublisherLink(state, p.publisher_id)
    : `<a href="${publisherHref(p.publisher_id)}" class="mp-publisher-link">${e(p.publisher_id)}</a>`;
  // Lean corpus first: `visible_item_count` is this exact number, decided by
  // the projection because the manifest it counts is median 53 KB and has no
  // business in a browse read.
  // `visible_item_count` is a generated column since 028, so the browse reads
  // carry it and no longer select `manifest` at all. The fallback survives for
  // a body-carrying row — `c?.visible`, not `c.visible`, because the manifest is
  // untrusted publisher-controlled JSONB and a null entry would throw here.
  const count = p.visible_item_count
    ?? p.manifest?.contents?.filter((c) => c?.visible !== false).length
    ?? 0;
  return `
    <article class="mp-card mp-pack-card">
      <a href="/packs/${e(p.slug)}" class="mp-card-name">${e(p.name)}</a>
      <p class="mp-card-desc">${e(p.description ?? '')}</p>
      <div class="mp-card-tags">
        ${tags.slice(0, 4).map((t) => `<span class="mp-tag">${e(t)}</span>`).join('')}
      </div>
      <div class="mp-card-meta">
        <span>by ${publisher}${renderCertifiedPill(p.publisher_certified)}</span>
        <span>v${e(String(p.version))}</span>
        ${count > 0 ? `<span>${count} item${count === 1 ? '' : 's'}</span>` : ''}
        ${(p.download_count ?? 0) > 0 ? `<span>${countLabel(p.download_count ?? 0, 'install')}</span>` : ''}
        <span class="mp-type-badge">Pack</span>
      </div>
      <div class="mp-card-actions">
        <a href="/packs/${e(p.slug)}" class="mp-install-btn">View &rarr;</a>
      </div>
    </article>
  `;
};

/** Distinct `pack_kind` values across the pack set — the pack list's primary
 *  facet (12C). */
const extractPackKinds = (packs: PackListing[]): string[] =>
  [...new Set(packs.map((p) => p.pack_kind).filter((k): k is string => Boolean(k)))].sort();

/** 12C — pack filter bar: pack_kind facet + tags + sort (parity with the recipe
 *  bar; reuses the shared `tagFilter`/sort state). Same server-paged caveat as
 *  `renderRecipeFilters` — the facets describe the rows in hand. */
const renderPackFilters = (state: MarketplaceState): string => {
  const kinds = facetChips(state, 'pack_kind', () => extractPackKinds(state.packs));
  const tags = facetChips(state, 'tag', () => extractTags(state.packs));
  const hasActiveFilter = state.packKindFilter || state.tagFilter;
  return `
    <div class="mp-filter-bar">
      ${kinds.length > 1 ? renderChipGroup('Kind', 'filter-pack-kind', kinds, state.packKindFilter) : ''}
      ${tags.length > 0 ? renderChipGroup('Tags', 'filter-tag', tags, state.tagFilter) : ''}
      ${renderSortGroup(state, ['popular', 'newest', 'updated', 'name'])}
      ${hasActiveFilter ? `
        <button class="mp-clear-filters" data-action="clear-filters">Clear filters</button>
      ` : ''}
    </div>
  `;
};

const renderPackList = (state: MarketplaceState): string => {
  // The rows ARE the answer — narrowed, ranked and paged by whoever produced
  // them (the search endpoint, or the SSR read). Re-filtering here would apply
  // the query twice.
  const filtered = state.packs;
  const { alreadyPaged, pageTotal, shownCount, totalCount } =
    listCounts(filtered, state.packs, state.totals?.packs, state.matched?.packs);
  const countLine = renderResultCount(shownCount, totalCount, 'packs');
  if (filtered.length === 0) {
    const hasFilters = state.search || state.packKindFilter || state.tagFilter;
    return `${countLine}<div class="mp-empty">${hasFilters ? 'Nothing matches those filters yet — try clearing one to widen the set.' : 'No packs published yet.'}</div>`;
  }
  const shown = alreadyPaged ? filtered : pageSlice(filtered, state.page);
  const page = clampPage(state.page, pageTotal);
  return `${countLine}<div class="mp-grid">${shown.map((p) => renderPackCard(p, state)).join('')}</div>`
    + renderPagination('/packs', page, pageTotal);
};

// ────────────────────────────────────────────────────────────────
// Top-level renderer
// ────────────────────────────────────────────────────────────────

export const renderMarketplace = (state: MarketplaceState): string => {
  if (state.loading) {
    return `
      <div class="marketplace">
        ${renderHeader(activeNavOf(state.view))}
        <div class="mp-loading">Loading marketplace…</div>
      </div>
    `;
  }
  if (state.error) {
    return `
      <div class="marketplace">
        ${renderHeader(activeNavOf(state.view))}
        <div class="mp-empty">
          <strong>Couldn't load the marketplace</strong>
          <p>Something went wrong reaching the store. Check your connection and try again.</p>
          <p class="mp-error-detail">${e(state.error)}</p>
          <button type="button" class="btn-cta" data-action="retry">Try again</button>
        </div>
      </div>
    `;
  }

  let body = '';
  switch (state.view) {
    case 'landing':
      body = renderLanding(state);
      break;
    case 'recipes':
      body = renderHero() + renderMarketplaceStats(state) + renderSearch(state) + renderRecipeFilters(state) + renderRecipeList(state);
      break;
    case 'facet':
      // No stats bar (those are corpus-wide and would misdescribe this page)
      // and no filter bar (the page IS the filter, and its chips would be drawn
      // from 48 rows). Just the subject, the set, and the way through it.
      body = renderFacetHero(state) + renderRecipeList(state);
      break;
    case 'facet-index':
      body = renderFacetIndex(state);
      break;
    case 'packs':
      body = renderHero() + renderSearch(state) + renderPackFilters(state) + renderPackList(state);
      break;
    case 'recipe-detail':
      body = renderRecipeDetail(state);
      break;
    case 'pack-detail':
      body = renderPackDetail(state);
      break;
    case 'publisher':
      body = renderPublisherPage(state);
      break;
  }

  return renderAppShell(body, activeNavOf(state.view));
};
